import moment from 'moment-timezone';
import { flatten, isEqual, isEmpty } from 'underscore';
import { cloneDeep, xorBy, uniqBy } from 'lodash';

import { LanguageModel } from '../../../Core/Localization/models/language.model';
import { MediaTypeModel } from './media-type.model';
import { MonitoringKeywordGroupModel } from './monitoring-keyword-group.model';
import { SentimentModel } from './sentiment.model';
import { SearchCriteriaQueryModel } from '../../../api/mentions/model/search-criteria-query.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { SentimentQueryModel } from '../../../api/mentions/model/sentiment-query.model';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { SentimentRangeModel } from '../../../api/mentions/model/sentiment-range.model';
import { JournodexSourceModel } from '../../../Common/Journodex/models/journodex-source.model';
import { SearchCriteriaSourcesEnum, searchCriteriaSourcesEnumToSourceTypeEnum } from '../../../api/mentions/enum/search-criteria-sources.enum';
import { MediaTypeEnum } from '../../../Common/Basic/enums/media-type.enum';
import { LinkedinSubTypeEnum } from '../../../Common/Basic/enums/linkedin-sub-type.enum';
import { TwitterSubTypeEnum } from '../../../Common/Basic/enums/twitter-sub-type.enum';
import { BlueskySubTypeEnum } from '../../../Common/Basic/enums/bluesky-sub-type.enum';
import { GeographyQueryModel } from '../../../api/mentions/model/geography-query.model';
import { JournodexGeographyModel } from '../../../Common/Journodex/models/journodex-geography.model';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { SearchCriteriaDateQueryModel } from '../../../api/mentions/model/search-criteria-date-query.model';
import { DateRangeModel } from '../../../api/mentions/model/date-range.model';
import { JournodexBasicModel } from '../../../api/mentions/model/journodex-basic.model';
import { ComparisonHelper } from '../helpers/comparison/comparison.helper';
import { MentionModel } from '../../../api/mentions/model/mention.model';
import { SearchContextsEnum } from '../enums/search-contexts.enum';
import { BasicNumberFilterModel } from '../../../api/mentions/model/basic-number-filter.model';
import { RangeModel } from '../../../Common/Filter/models/range.model';
import { MonitoringAdvancedBooleanModel } from './monitoring-advanced-boolean.model';
import { AdvancedBooleanExpressionInterface } from '../../../api/mentions/model/advanced-boolean-expression.interface';
import { monitoringFiltersEnum } from '../enums/monitoring-filters.enum';
import { InvalidFiltersError } from '../errors/invalid-filters.error';
import { Mention } from '../../../Common/Mentions/models/mention';
import { DateQueryDateTypeEnum } from '../../../api/mentions/enum/date-query-date-type.enum';
import { ProminenceRangeFilterApiModel } from '../../../api/mentions/model/prominence-filter-api.model';
import { FilterSourceModel } from './filter-source.model';
import { FilterSourceApiModel } from '../../../api/mentions/model/filter-source-api.model';
import { SourceTypeEnum } from '../../../api/source/enums/source-type.enum';
import { engagementFilterApiKeys, EngagementFilterApiModel } from '../../../Common/Filter/models/engagement-filter.model';
import { SocialSearchRequestModel } from '../../../api/mentions/model/social-search-request.model';
import { isPositiveInteger } from '../../../Common/Misc/helpers/formatting/number.helper';
import { SearchMode } from '../enums/search-mode.enum';
import { searchContextToApiMap } from './search-contexts.map';
import { BasicStringFilterModel } from '../../../api/mentions/model/basic-string-filter.model';
import { DiscoverListFilterModel } from '../../../api/mentions/model/discover-list-filter.model';
import { SampleFilterModel } from './sample-filter.model';
import { DrillDownTermFilter } from './drill-down-term-filter.model';
import { DrillDownTermFilterApi } from '../../../api/mentions/model/drill-down-term-filter-api.model';
import { BroadcastSubTypeEnum } from '../../../Common/Basic/enums/broadcast-sub-type.enum';


export class MentionSearchQueryModel {
    caseSensitive: boolean;
    searchMode: SearchMode;

    /**
     * Deprecated - use {@link MentionSearchQueryModel.isArchivedMention}
     * Only used for bulk removal of archived critical mentions
     */
    isArchivedCriticalMention: boolean;
    isArchivedMention: boolean;

    dateQuery: DateQueryModel;
    keywordGroups: MonitoringKeywordGroupModel[];
    expertBooleanQuery: MonitoringAdvancedBooleanModel;
    ave: RangeModel;
    audienceReach: RangeModel;

    includedMediaTypes: MediaTypeModel[];
    excludedMediaTypes: MediaTypeModel[];

    includedTweetTypes: MediaTypeModel[];
    excludedTweetTypes: MediaTypeModel[];

    includedLinkedinTypes: MediaTypeModel[];
    excludedLinkedinTypes: MediaTypeModel[];

    includedBlueskyTypes: MediaTypeModel[];
    excludedBlueskyTypes: MediaTypeModel[];

    includedTvTypes: MediaTypeModel[];
    excludedTvTypes: MediaTypeModel[];

    sentiments: SentimentModel[];
    engagementFilter: EngagementFilterApiModel = {
        engagementRate: {},
        engagementTotal: {},
        amplification: {},
        conversation: {},
        applause: {},
        view: {}
    };
    enableEngagementInsights: boolean;

    includedLanguages: LanguageModel[];
    excludedLanguages: LanguageModel[];

    folder: BasicNumberFilterModel;

    tag: BasicNumberFilterModel;
    tagGroup: BasicNumberFilterModel;
    sourceGroup: BasicNumberFilterModel;
    // social topics/subscriptions
    subscription: BasicNumberFilterModel;
    agent: BasicNumberFilterModel;
    /** Curation agents that have acted on the mention - advanced monitoring's aiAgent filter */
    aiAgent: BasicNumberFilterModel;
    /** Sources those agents acted from - advanced monitoring's aiAgentSource filter */
    aiAgentSource: BasicNumberFilterModel;
    /** Ad hoc agent runs that have acted on the mention - advanced monitoring's aiAgentAdhoc filter */
    aiAgentAdhoc: BasicNumberFilterModel;
    theme: BasicNumberFilterModel;


    /**
     * Monitoring Search specific, populated by following api properties
     * SearchCriteriaQueryModel.outlet
     * SearchCriteriaQueryModel.contact
     * SearchCriteriaQueryModel.url
     * SearchCriteriaQueryModel.sourceGroup
     */
    includedSources: JournodexSourceModel[];
    excludedSources: JournodexSourceModel[];

    filterSources: FilterSourceModel[];

    includedRegions: JournodexGeographyModel[];
    rawIncludedRegions: GeographyQueryModel;
    excludedRegions: JournodexGeographyModel[];
    rawExcludedRegions: GeographyQueryModel;

    hashtags: BasicStringFilterModel;

    includedProminenceFilter: ProminenceRangeFilterApiModel[];
    excludedProminenceFilter: ProminenceRangeFilterApiModel[];

    domainAuthority: RangeModel;

    agentId: number;
    themeId: number;
    socialSearchRequest: SocialSearchRequestModel;

    // frontend should not change this property
    drillDownFilters: DrillDownTermFilter[];

    discoverListFilter: DiscoverListFilterModel;

    sampleFilter: SampleFilterModel;

    syndicateIds?: BasicStringFilterModel;


    /**
     * Class constructor
     *
     * @param apiEntity
     */
    constructor(apiEntity?: SearchCriteriaQueryModel, isCombinedQuery?: boolean) {
        const hasApiEntity: boolean = apiEntity !== null && typeof apiEntity === 'object';

        if (hasApiEntity) {
            this.constructFromApiEntity(apiEntity, isCombinedQuery);

        } else {
            this.constructDefault();
        }
    }

    /**
     * Static constructor of Negative sentiment range
     */
    static negativeSentimentRange(): SentimentRangeModel {
        return {
            min: -1,
            max: -0.3
        };
    }

    /**
     * Static constructor of Negative sentiment range
     */
    static neutralSentimentRange(): SentimentRangeModel {
        return {
            min: -0.2,
            max: 0.2
        };
    }

    /**
     * Static constructor of Negative sentiment range
     */
    static positiveSentimentRange(): SentimentRangeModel {
        return {
            min: 0.3,
            max: 1
        };
    }

    /**
     * Determines if a raw region has any geography codes
     *
     * @param rawRegion region without localization strings
     *
     * @returns boolean
     */
    static isRawRegionEmpty(rawRegion: GeographyQueryModel): boolean {
        if (!rawRegion) {
            return true;
        }

        if (rawRegion.continents && rawRegion.continents.length) {
            return false;
        }
        if (rawRegion.countries && rawRegion.countries.length) {
            return false;
        }
        if (rawRegion.provinces && rawRegion.provinces.length) {
            return false;
        }
        if (rawRegion.counties && rawRegion.counties.length) {
            return false;
        }
        if (rawRegion.cities && rawRegion.cities.length) {
            return false;
        }

        return true;
    }
    /**
     * Extract selected geographic choices and load them into search criteria model for API call
     *
     * @param listOfRegions
     */
    public static extractRegions(listOfRegions: JournodexGeographyModel[]): GeographyQueryModel {
        const hasListOfRegions: boolean = Array.isArray(listOfRegions);
        const regionSet: GeographyQueryModel = {
            cities: [],
            continents: [],
            counties: [],
            countries: [],
            provinces: [],
        };

        if (hasListOfRegions) {
            for (let i = 0; i < listOfRegions.length; i++) {
                if (listOfRegions[i].id == null) {
                    continue;
                }
                switch (listOfRegions[i].geographicType) {
                    case GeographyTypesEnum.city:
                        regionSet.cities.push(listOfRegions[i].id);
                        break;

                    case GeographyTypesEnum.county:
                        regionSet.counties.push(listOfRegions[i].id);
                        break;

                    case GeographyTypesEnum.province:
                        regionSet.provinces.push(listOfRegions[i].id);
                        break;

                    case GeographyTypesEnum.country:
                        regionSet.countries.push(listOfRegions[i].id);
                        break;

                    case GeographyTypesEnum.continent:
                        regionSet.continents.push(listOfRegions[i].id);
                        break;
                }
            }
        }

        return regionSet;
    }

    /** Validate the range filter to see if it's safe to applied to the search query before searching
     * @param searchFilter The source search query filter
     * @param filter The range filter to apply to the search query
     */
    static validateRangeFilter(searchRangeFilter: RangeModel, filter: RangeModel): boolean {
        let min = searchRangeFilter.min;
        let max = searchRangeFilter.max;

        if (!isNaN(min) && !isNaN(max)) {
            if (!isNaN(filter.min)) {
                min = Math.max(min, filter.min);
            }
            if (!isNaN(filter.max)) {
                max = Math.min(max, filter.max);
            }
            if (max < min) {
                return false;
            }
        } else if (!isNaN(min)) {
            if (!isNaN(filter.min)) {
                min = Math.max(min, filter.min);
            }
            if (!isNaN(filter.max)) {
                max = filter.max;

                if (max < min) {
                    return false;
                }
            }
        } else if (!isNaN(max)) {
            if (!isNaN(filter.max)) {
                max = Math.min(max, filter.max);
            }
            if (!isNaN(filter.min)) {
                min = filter.min;

                if (max < min) {
                    return false;
                }
            }
        }
        return true;
    }

    public static rangeModelEquals(a: RangeModel, b: RangeModel) {
        if (isPositiveInteger(a.min) !== isPositiveInteger(b.min)) {
            return false;
        }

        if (isPositiveInteger(a.max) !== isPositiveInteger(b.max)) {
            return false;
        }

        if (
            isPositiveInteger(a.min)  && isPositiveInteger(b.min) &&
            a.min !== b.min
        ) {
            return false;
        }

        if (
            isPositiveInteger(a.max)  && isPositiveInteger(b.max) &&
            a.max !== b.max
        ) {
            return false;
        }

        return true;
    }

    /**
     * Creates a filtered search query object
     *
     * @param search The source search query
     * @param filters The filters to apply to the search query
     * @throws InvalidFiltersError Contains a list of the invalid filter types
     */
    static createFiltered(
        search: MentionSearchQueryModel,
        filters: MentionSearchQueryModel,
        includeDate: boolean = true
    ): MentionSearchQueryModel {
        const output = search.clone();
        const errorTypes: monitoringFiltersEnum[] = [];

        if (includeDate && filters.dateQuery) {
            output.dateQuery = filters.dateQuery;
        }

        // if either the filters or original search object is using expert search mode, all keywords must be converted to expert
        if (filters.expertBooleanQuery?.filter.length > 0 || output.expertBooleanQuery?.filter.length > 0) {
            output.searchMode = SearchMode.expert;

            // filter expert -> search expert
            if (filters.expertBooleanQuery?.filter.length > 0) {
                if (!output.expertBooleanQuery) {
                    output.expertBooleanQuery = new MonitoringAdvancedBooleanModel();
                }
                if (output.expertBooleanQuery.filter.length > 0) {
                    output.expertBooleanQuery.filter = `(${output.expertBooleanQuery.filter}) AND (${filters.expertBooleanQuery.filter})`;
                } else {
                    output.expertBooleanQuery.filter = `(${filters.expertBooleanQuery.filter})`;
                }
                output.expertBooleanQuery.searchContexts = filters.expertBooleanQuery.searchContexts;
            } else if (filters.keywordGroups?.some(group => group.isAnyKeywordSet())) {
                // filter basic -> search expert
                if (output.expertBooleanQuery.filter.length > 0) {
                    output.expertBooleanQuery.filter = `(${output.expertBooleanQuery.filter})`;
                    // NOT only keyword groups will be converted into AND NOT (keywords)
                    // If we add AND to this case, the query will be AND AND NOT
                    if (filters.hasPositiveKeywordGroups()) {
                        output.expertBooleanQuery.filter += ' AND ';
                    }
                    output.expertBooleanQuery.filter += `${output.expertBooleanQuery.convertBasicQueryToExpert(filters.keywordGroups)}`;
                } else {
                    output.expertBooleanQuery.filter = output.expertBooleanQuery.convertBasicQueryToExpert(filters.keywordGroups);
                }
                output.expertBooleanQuery.searchContexts = filters.keywordGroups[0].searchContexts;
            }

            // search basic -> search expert
            if (output.keywordGroups?.some(group => group.isAnyKeywordSet())) {
                const keywords = output.expertBooleanQuery.convertBasicQueryToExpert(output.keywordGroups);
                if (output.expertBooleanQuery.filter.length > 0) {
                    output.expertBooleanQuery.filter += ` AND ${keywords}`;
                } else {
                    output.expertBooleanQuery.filter = keywords;
                }
            }
        } else if (filters.keywordGroups?.some(group => group.isAnyKeywordSet())) {
            output.searchMode = SearchMode.standard;
            output.keywordGroups.push(...filters.keywordGroups);
        }

        output.keywordGroups = output.keywordGroups.filter(keywordGroups => {
            return keywordGroups.and?.length ||
                keywordGroups.or?.length ||
                keywordGroups.not?.length ||
                keywordGroups.keywordValidations?.length ||
                keywordGroups.searchContexts?.length;
        });

        if (filters.includedMediaTypes.length) {
            if (output.includedMediaTypes?.length) {
                output.includedMediaTypes = output.includedMediaTypes.filter(mediaType => {
                    return !!filters.includedMediaTypes.find(mediaTypeFilter => mediaType.id === mediaTypeFilter.id);
                });

                if (output.includedMediaTypes.length === 0) {
                    errorTypes.push(monitoringFiltersEnum.mediaTypes);
                }
            } else {
                output.includedMediaTypes = [...filters.includedMediaTypes];
            }
        } else if (filters.excludedMediaTypes?.length) {
            if (output.includedMediaTypes?.length) {
                output.includedMediaTypes = output.includedMediaTypes.filter(item => !filters.excludedMediaTypes.some(type => type.id === item.id));
            } else if (output.excludedMediaTypes?.length) {
                output.excludedMediaTypes = [...new Set([...filters.excludedMediaTypes, ...output.excludedMediaTypes])];
            } else {
                output.excludedMediaTypes = [...filters.excludedMediaTypes];
            }
        };

        const twitterParent = filters.includedMediaTypes.find(x => x.id === MediaTypeEnum.TWITTER);

        if (twitterParent?.children?.length > 0) {
            const twitterChildren = twitterParent.children.map(option => option.value);
            output.includedTweetTypes = [...twitterChildren];
        } else if (filters.includedTweetTypes?.length > 0) {
            output.includedTweetTypes = [...filters.includedTweetTypes];
        }

        const excludeTwitterParent = filters.excludedMediaTypes.find(x => x.id === MediaTypeEnum.TWITTER);

        if (excludeTwitterParent?.children?.length > 0) {
            const twitterChildren = excludeTwitterParent.children.map(option => option.value);
            output.excludedTweetTypes = [...twitterChildren];
        } else if (filters.excludedTweetTypes?.length > 0) {
            output.excludedTweetTypes = [...filters.excludedTweetTypes];
        }

        const linkedinParent = filters.includedMediaTypes.find(x => x.id === MediaTypeEnum.LINKEDIN);
        if (linkedinParent?.children?.length > 0) {
            const linkedinChildren = linkedinParent.children.map(option => option.value);
            output.includedLinkedinTypes = [...linkedinChildren];
        } else if (filters.includedLinkedinTypes?.length > 0) {
            output.includedLinkedinTypes = [...filters.includedLinkedinTypes];
        }

        const excludeLinkedinParent = filters.excludedMediaTypes.find(x => x.id === MediaTypeEnum.LINKEDIN);
        if (excludeLinkedinParent?.children?.length > 0) {
            const linkedinChildren = excludeLinkedinParent.children.map(option => option.value);
            output.excludedLinkedinTypes = [...linkedinChildren];
        } else if (filters.excludedLinkedinTypes?.length > 0) {
            output.excludedLinkedinTypes = [...filters.excludedLinkedinTypes];
        }

        const blueskyParent = filters.includedMediaTypes.find(x => x.id === MediaTypeEnum.BLUESKY);
        if (blueskyParent?.children?.length > 0) {
            const blueskyChildren = blueskyParent.children.map(option => option.value);
            output.includedBlueskyTypes = [...blueskyChildren];
        } else if (filters.includedBlueskyTypes?.length > 0) {
            output.includedBlueskyTypes = [...filters.includedBlueskyTypes];
        }

        const excludeBlueskyParent = filters.excludedMediaTypes.find(x => x.id === MediaTypeEnum.BLUESKY);
        if (excludeBlueskyParent?.children?.length > 0) {
            const blueskyChildren = excludeBlueskyParent.children.map(option => option.value);
            output.excludedBlueskyTypes = [...blueskyChildren];
        } else if (filters.excludedBlueskyTypes?.length > 0) {
            output.excludedBlueskyTypes = [...filters.excludedBlueskyTypes];
        }

        const tvParent = filters.includedMediaTypes.find(x => x.id === MediaTypeEnum.TV);
        if (tvParent?.children?.length > 0) {
            const tvChildren = tvParent.children.map(option => option.value);
            output.includedTvTypes = [...tvChildren];
        } else if (filters.includedTvTypes?.length > 0) {
            output.includedTvTypes = [...filters.includedTvTypes];
        }

        const excludeTvParent = filters.excludedMediaTypes.find(x => x.id === MediaTypeEnum.TV);
        if (excludeTvParent?.children?.length > 0) {
            const tvChildren = excludeTvParent.children.map(option => option.value);
            output.excludedTvTypes = [...tvChildren];
        } else if (filters.excludedTvTypes?.length > 0) {
            output.excludedTvTypes = [...filters.excludedTvTypes];
        }

        if (filters.isArchivedMention !== undefined && filters.isArchivedMention !== null) {
            output.isArchivedMention = filters.isArchivedMention;
        }

        if (filters.sentiments && filters.sentiments.length > 0) {
            if (output.sentiments && output.sentiments.length > 0) {
                output.sentiments = output.sentiments.filter(sentiment => {
                    return !!filters.sentiments.find(sentimentFilter => sentiment.id === sentimentFilter.id);
                });

                if (output.sentiments.length === 0) {
                    errorTypes.push(monitoringFiltersEnum.sentiment);
                }
            } else {
                output.sentiments = [...filters.sentiments];
            }
        }

        if (filters.includedLanguages && filters.includedLanguages.length > 0) {
            if (output.includedLanguages && output.includedLanguages.length > 0) {
                output.includedLanguages = output.includedLanguages.filter(lang => {
                    return !!filters.includedLanguages.find(langFilter => lang.id === langFilter.id);
                });

                if (output.includedLanguages.length === 0) {
                    errorTypes.push(monitoringFiltersEnum.languages);
                }
            } else if (output.excludedLanguages && output.excludedLanguages.length > 0) {
                const filtersMinusExcluded = filters.includedLanguages.filter(lang => {
                    return !output.excludedLanguages.find(excludeLang => excludeLang.id === lang.id);
                });

                if (filtersMinusExcluded.length === 0) {
                    errorTypes.push(monitoringFiltersEnum.languages);
                } else {
                    output.includedLanguages = filtersMinusExcluded;
                    output.excludedLanguages = [];
                }
            } else {
                output.includedLanguages = [...filters.includedLanguages];
                output.excludedLanguages = [];
            }
        } else if (filters.excludedLanguages?.length) {
            if (output.excludedLanguages?.length ) {
                filters.excludedLanguages.forEach(filterItem => {
                    if (!output.excludedLanguages.some(outputItem => outputItem.id === filterItem.id)) {
                        output.excludedLanguages.push(filterItem);
                    }
                });

                if (output.excludedLanguages.length === 0) {
                    errorTypes.push(monitoringFiltersEnum.languages);
                }
            } else if (output.includedLanguages?.length ) {
                const outputMinusExcluded = output.includedLanguages.filter(lang => {
                    return !filters.excludedLanguages.find(excludeLang => excludeLang.id === lang.id);
                });

                if (outputMinusExcluded.length === 0) {
                    errorTypes.push(monitoringFiltersEnum.languages);
                } else {
                    output.includedLanguages = outputMinusExcluded;
                    output.excludedLanguages = [];
                }
            } else {
                output.excludedLanguages = [...filters.excludedLanguages];
                output.includedLanguages = [];
            }
        }

        if (filters.includedRegions && filters.includedRegions.length > 0) {
            const filterHasContinents = !!filters.includedRegions.find(region => region.geographicType === GeographyTypesEnum.continent);
            const filterHasCountries = !!filters.includedRegions.find(region => region.geographicType === GeographyTypesEnum.country);
            const filterHasProvinces = !!filters.includedRegions.find(region => region.geographicType === GeographyTypesEnum.province);
            const filterHasCities = !!filters.includedRegions.find(region => region.geographicType === GeographyTypesEnum.city);
            const filterHasCounties = !!filters.includedRegions.find(region => region.geographicType === GeographyTypesEnum.county);

            let outputHasContinents = filterHasContinents;
            let outputHasCountries = filterHasCountries;
            let outputHasProvinces = filterHasProvinces;
            let outputHasCities = filterHasCities;
            let outputHasCounties = filterHasCounties;

            const includedRegions = output.includedRegions || [];
            const excludedRegions = output.excludedRegions || [];
            const rawIncludedRegions = output.rawIncludedRegions || {};
            const rawExcludedRegions = output.rawExcludedRegions || {};
            const rawIncludeEmpty = MentionSearchQueryModel.isRawRegionEmpty(rawIncludedRegions);
            const rawExcludeEmpty = MentionSearchQueryModel.isRawRegionEmpty(rawExcludedRegions);

            if (includedRegions.length > 0 || excludedRegions.length > 0) {
                output.includedRegions = filters.includedRegions.filter(filterRegion => {
                    const isIncluded = includedRegions.length === 0 || includedRegions.find(region => filterRegion.isChildOf(region));
                    const isExcluded = excludedRegions.length === 0 || excludedRegions.find(region => filterRegion.isChildOf(region));

                    return isIncluded && !isExcluded;
                });

                outputHasContinents = !!output.includedRegions.find(region => region.geographicType === GeographyTypesEnum.continent);
                outputHasCountries = !!output.includedRegions.find(region => region.geographicType === GeographyTypesEnum.country);
                outputHasProvinces = !!output.includedRegions.find(region => region.geographicType === GeographyTypesEnum.province);
                outputHasCities = !!output.includedRegions.find(region => region.geographicType === GeographyTypesEnum.city);
                outputHasCounties = !!output.includedRegions.find(region => region.geographicType === GeographyTypesEnum.county);
            } else if (!rawIncludeEmpty || !rawExcludeEmpty) {
                const validFilterRegions = filters.includedRegions.filter(filterRegion => {
                    const isIncluded = rawIncludeEmpty || filterRegion.isChildOfRawList(rawIncludedRegions);
                    const isExcluded = filterRegion.isChildOfRawList(rawExcludedRegions);

                    return isIncluded && !isExcluded;
                });
                output.rawIncludedRegions = MentionSearchQueryModel.extractRegions(validFilterRegions);

                outputHasContinents = output.rawIncludedRegions.continents.length > 0;
                outputHasCountries = output.rawIncludedRegions.countries.length > 0;
                outputHasProvinces = output.rawIncludedRegions.provinces.length > 0;
                outputHasCities = output.rawIncludedRegions.cities.length > 0;
                outputHasCounties = output.rawIncludedRegions.counties.length > 0;
            } else {
                output.includedRegions = [...filters.includedRegions];
            }

            if (outputHasContinents !== filterHasContinents) {
                errorTypes.push(monitoringFiltersEnum.continent);
            }

            if (outputHasCountries !== filterHasCountries) {
                errorTypes.push(monitoringFiltersEnum.country);
            }

            if (outputHasProvinces !== filterHasProvinces) {
                errorTypes.push(monitoringFiltersEnum.province);
            }

            if (outputHasCities !== filterHasCities) {
                errorTypes.push(monitoringFiltersEnum.city);
            }

            if (outputHasCounties !== filterHasCounties) {
                errorTypes.push(monitoringFiltersEnum.county);
            }

        // if we want to merge two raw search MentionSearchQueryModel
        } else if (filters.rawIncludedRegions) {
            const rawIncludedRegions = output.rawIncludedRegions || {};
            const rawExcludedRegions = output.rawExcludedRegions || {};

            let filterHasContinents = filters.rawIncludedRegions.continents.length > 0;
            let filterHasCountries = filters.rawIncludedRegions.countries.length > 0;
            let filterHasProvinces = filters.rawIncludedRegions.provinces.length > 0;
            let filterHasCities = filters.rawIncludedRegions.cities.length > 0;
            let filterHasCounties = filters.rawIncludedRegions.counties.length > 0;

            let outputHasContinents = output.rawIncludedRegions.continents.length > 0;
            let outputHasCountries = output.rawIncludedRegions.countries.length > 0;
            let outputHasProvinces = output.rawIncludedRegions.provinces.length > 0;
            let outputHasCities = output.rawIncludedRegions.cities.length > 0;
            let outputHasCounties = output.rawIncludedRegions.counties.length > 0;

            // if the source (ex: topic) has regions defined, but there was no region filters applied.. continue
            if ((!filterHasContinents && !filterHasCountries && !filterHasProvinces && !filterHasCities && !filterHasCounties) &&
                (outputHasContinents || outputHasCountries  || outputHasProvinces  || outputHasCities  || outputHasCounties)) {
                filterHasContinents = outputHasContinents;
                filterHasCountries = outputHasCountries;
                filterHasProvinces = outputHasProvinces;
                filterHasCities = outputHasCities;
                filterHasCounties = outputHasCounties;
            } else {
                // else compare the source & filter regions applied
                if (rawIncludedRegions.continents?.length) {
                    output.rawIncludedRegions.continents = filters.rawIncludedRegions.continents?.filter(continentId => {
                        const isIncluded = rawIncludedRegions.continents.indexOf(continentId) > -1;
                        const isExcluded = rawExcludedRegions?.continents?.indexOf(continentId) > -1;

                        return isIncluded && !isExcluded;
                    }) || [];
                    outputHasContinents = output.rawIncludedRegions.continents.length > 0;
                }

                if (rawIncludedRegions.countries?.length) {
                    output.rawIncludedRegions.countries = filters.rawIncludedRegions.countries?.filter(countryId => {
                        const isIncluded = rawIncludedRegions.countries.indexOf(countryId) > -1;
                        const isExcluded = rawExcludedRegions?.countries?.indexOf(countryId) > -1;

                        return isIncluded && !isExcluded;
                    }) || [];
                    outputHasCountries = output.rawIncludedRegions.countries.length > 0;
                }

                if (rawIncludedRegions.provinces?.length) {
                    output.rawIncludedRegions.provinces = filters.rawIncludedRegions.provinces?.filter(provinceId => {
                        const isIncluded = rawIncludedRegions.provinces.indexOf(provinceId) > -1;
                        const isExcluded = rawExcludedRegions?.provinces?.indexOf(provinceId) > -1;

                        return isIncluded && !isExcluded;
                    }) || [];
                    outputHasProvinces = output.rawIncludedRegions.provinces.length > 0;
                }

                if (rawIncludedRegions.cities?.length) {
                    output.rawIncludedRegions.cities = filters.rawIncludedRegions.cities?.filter(cityId => {
                        const isIncluded = rawIncludedRegions.cities.indexOf(cityId) > -1;
                        const isExcluded = rawExcludedRegions?.cities?.indexOf(cityId) > -1;

                        return isIncluded && !isExcluded;
                    }) || [];
                    outputHasCities = output.rawIncludedRegions.cities.length > 0;
                }

                if (rawIncludedRegions.counties?.length) {
                    output.rawIncludedRegions.counties = filters.rawIncludedRegions.counties?.filter(countyId => {
                        const isIncluded = rawIncludedRegions.counties.indexOf(countyId) > -1;
                        const isExcluded = rawExcludedRegions?.counties?.indexOf(countyId) > -1;

                        return isIncluded && !isExcluded;
                    }) || [];
                    outputHasCounties = output.rawIncludedRegions.counties.length > 0;
                }
            }

            if (outputHasContinents !== filterHasContinents) {
                errorTypes.push(monitoringFiltersEnum.continent);
            }

            if (outputHasCountries !== filterHasCountries) {
                errorTypes.push(monitoringFiltersEnum.country);
            }

            if (outputHasProvinces !== filterHasProvinces) {
                errorTypes.push(monitoringFiltersEnum.province);
            }

            if (outputHasCities !== filterHasCities) {
                errorTypes.push(monitoringFiltersEnum.city);
            }

            if (outputHasCounties !== filterHasCounties) {
                errorTypes.push(monitoringFiltersEnum.county);
            }
        }

        if (filters.excludedRegions?.length) {
            output.excludedRegions = [...filters.excludedRegions];
        }

        if (filters.filterSources && filters.filterSources.length) {
            output.filterSources.push(...filters.filterSources);
        }

        if (filters.includedSources && filters.includedSources.length > 0) {
            output.filterSources.push(
                ...filters.includedSources.map(source => {
                    return new FilterSourceModel({
                        type: searchCriteriaSourcesEnumToSourceTypeEnum.get(source.entityType),
                        value: {
                            // @HACK b/e needs the double quotes to fetch correct facets and results -- refer to D20061 NEP-8747
                            name: `"${source.entityName}"`
                        }
                    } as FilterSourceApiModel);
                })
            );
        } else if (filters.excludedSources?.length) {
            output.excludedSources = [...filters.excludedSources];
            output.filterSources = output.filterSources.filter(
                source => !filters.excludedSources.some(filterSource =>
                    searchCriteriaSourcesEnumToSourceTypeEnum.get(filterSource.entityType) === source.type
                    && filterSource.entityName === source.value?.name
                )
            );
        }

        output.filterSources = uniqBy(output.filterSources, source => JSON.stringify(source.toApiEntity()));

        if (filters.audienceReach && !(isNaN(filters.audienceReach.min) && isNaN(filters.audienceReach.max))) {
            if (output.audienceReach) {
                const isValid = MentionSearchQueryModel.validateRangeFilter(output.audienceReach, filters.audienceReach);
                if (isValid) {
                    output.audienceReach = filters.audienceReach;
                } else {
                    errorTypes.push(monitoringFiltersEnum.audience);
                }
            } else {
                output.audienceReach = filters.audienceReach;
            }
        }

        if (filters.ave && !(isNaN(filters.ave.min) && isNaN(filters.ave.max))) {
            if (output.ave) {
                const isValid = MentionSearchQueryModel.validateRangeFilter(output.ave, filters.ave);
                if (isValid) {
                    output.ave = filters.ave;
                } else {
                    errorTypes.push(monitoringFiltersEnum.ave);
                }
            } else {
                output.ave = filters.ave;
            }
        }

        if (filters.domainAuthority) {
            if (output.domainAuthority) {
                const isValid = MentionSearchQueryModel.validateRangeFilter(output.domainAuthority, filters.domainAuthority);
                if (isValid) {
                    output.domainAuthority = filters.domainAuthority;
                } else {
                    errorTypes.push(monitoringFiltersEnum.domainAuthority);
                }
            } else {
                output.domainAuthority = filters.domainAuthority;
            }
        }

        if (filters.folder && filters.folder.include && filters.folder.include.length > 0) {
            if (output.folder) {
                const includedFolderIds = output.folder.include || [];
                const excludedFolderIds = output.folder.exclude || [];
                const filterFolderIds = filters.folder.include;

                output.folder.include = filterFolderIds.filter(filterFolderId => {
                    const isIncluded = includedFolderIds.length < 1 || !!includedFolderIds.find(includeFolderId => +filterFolderId === +includeFolderId);
                    const isExcluded = !!excludedFolderIds.find(excludeFolderId => +filterFolderId === +excludeFolderId);

                    return isIncluded && !isExcluded;
                });

                if (output.folder.include.length < 1) {
                    errorTypes.push(monitoringFiltersEnum.folders);
                }
            } else {
                output.folder = filters.folder;
            }
        }

        if (filters.tag) {
            let hasIncludes, hasExcludes = false;
            if (filters.tag.include && filters.tag.include.length > 0) {
                hasIncludes = true;
                if (output.tag) {
                    const includedTagIds = output.tag.include || [];
                    const excludedTagIds = output.tag.exclude || [];
                    const filterTagIds = filters.tag.include;

                    output.tag.include = filterTagIds.filter(filterTagId => {
                        const isIncluded = includedTagIds.length < 1 || !!includedTagIds.find(includeTagId => +filterTagId === +includeTagId);
                        const isExcluded = !!excludedTagIds.find(excludeTagId => +filterTagId === +excludeTagId);

                        return isIncluded && !isExcluded;
                    });

                    if (output.tag.include.length < 1) {
                        errorTypes.push(monitoringFiltersEnum.tags);
                    }
                } else {
                    output.tag = filters.tag;
                }
            } else if (filters.tag.exclude && filters.tag.exclude.length > 0) {
                hasExcludes = true;
                output.tag.exclude = filters.tag.exclude;
            }

            if (hasIncludes && hasExcludes) {
                errorTypes.push(monitoringFiltersEnum.tags);
            }
        }

        if (filters.hashtags?.include?.length || filters.hashtags?.exclude?.length) {
            output.hashtags = {
                include:  !!filters.hashtags?.include?.length ? [...filters.hashtags?.include] : [],
                exclude:  !!filters.hashtags?.exclude?.length ? [...filters.hashtags?.exclude] : [],
            };
        }

        if (filters.tagGroup && filters.tagGroup.include && filters.tagGroup.include.length > 0) {
            if (output.tagGroup) {
                const includedTagGroupIds = output.tagGroup.include || [];
                const excludedTagGroupIds = output.tagGroup.exclude || [];
                const filterTagGroupIds = filters.tagGroup.include;

                output.tagGroup.include = filterTagGroupIds.filter(filterTagGroupId => {
                    const isIncluded = includedTagGroupIds.length < 1 || !!includedTagGroupIds.find(
                        includeTagGroupId => +filterTagGroupId === +includeTagGroupId
                    );
                    const isExcluded = !!excludedTagGroupIds.find(excludeTagGroupId => +filterTagGroupId === +excludeTagGroupId);

                    return isIncluded && !isExcluded;
                });

                if (output.tagGroup.include.length < 1) {
                    errorTypes.push(monitoringFiltersEnum.tagGroups);
                }
            } else {
                output.tagGroup = filters.tagGroup;
            }
        }
        if (filters.sourceGroup && filters.sourceGroup.include && filters.sourceGroup.include.length > 0) {
            filters.sourceGroup.include.forEach(sourceGroupId => {
                output.filterSources.push(
                    new FilterSourceModel({
                        type: SourceTypeEnum.sourceGroup.replace(' ', '_'),
                        value: {
                            id: sourceGroupId.toString()
                        }
                    } as FilterSourceApiModel)
                );
            });
        }

        engagementFilterApiKeys.forEach(key => {
            const value = filters.engagementFilter[key];
            if (value && (!isNaN(value?.min) || !isNaN(value?.max))) {
                if (output.engagementFilter[key]) {
                    const isValid = MentionSearchQueryModel.validateRangeFilter(output.engagementFilter[key], value);
                    if (isValid) {
                        output.engagementFilter[key] = value;
                    } else {
                        if (key === 'engagementRate') {
                            errorTypes.push(monitoringFiltersEnum.engagementRate);
                        } else {
                            errorTypes.push(monitoringFiltersEnum.engagementTotal);
                        }
                    }
                } else {
                    output.engagementFilter[key] = value;
                }
            }
        });

        if (filters.agent && filters.agent.include && filters.agent.include.length > 0) {
            if (output.agent) {
                const includedAgentIds = output.agent.include || [];
                const excludedAgentIds = output.agent.exclude || [];
                const filterAgentIds = filters.agent.include;

                output.agent.include = filterAgentIds.filter(filterAgentId => {
                    const isIncluded = includedAgentIds.length < 1 || !!includedAgentIds.find(
                        includeAgentId => +filterAgentId === +includeAgentId
                    );
                    const isExcluded = !!excludedAgentIds.find(excludeAgentId => +filterAgentId === +excludeAgentId);

                    return isIncluded && !isExcluded;
                });

                if (output.agent.include.length < 1) {
                    errorTypes.push(monitoringFiltersEnum.agent);
                }
            } else {
                output.agent = filters.agent;
            }
        }

        if (filters.theme && filters.theme.include && filters.theme.include.length > 0) {
            if (output.theme) {
                const includedThemeIds = output.theme.include || [];
                const excludedThemeIds = output.theme.exclude || [];
                const filterThemeIds = filters.theme.include;

                output.theme.include = filterThemeIds.filter(filterThemeId => {
                    const isIncluded = includedThemeIds.length < 1 || !!includedThemeIds.find(
                        includeThemeId => +filterThemeId === +includeThemeId
                    );
                    const isExcluded = !!excludedThemeIds.find(excludeThemeId => +filterThemeId === +excludeThemeId);

                    return isIncluded && !isExcluded;
                });

                if (output.theme.include.length < 1) {
                    errorTypes.push(monitoringFiltersEnum.theme);
                }
            } else {
                output.theme = filters.theme;
            }
        }

        [
            'aiAgent',
            'aiAgentSource',
            'aiAgentAdhoc'
        ].forEach((field: 'aiAgent' | 'aiAgentSource' | 'aiAgentAdhoc') => {
            if (filters[field] && filters[field].include && filters[field].include.length > 0) {
                if (output[field]) {
                    const includedAIAgentIds = output[field].include || [];
                    const excludedAIAgentIds = output[field].exclude || [];
                    const filterAIAgentIds = filters[field].include;

                    output[field].include = filterAIAgentIds.filter(filterAIAgentId => {
                        const isIncluded = includedAIAgentIds.length < 1 || !!includedAIAgentIds.find(
                            includeAIAgentId => +filterAIAgentId === +includeAIAgentId
                        );
                        const isExcluded = !!excludedAIAgentIds.find(excludeAIAgentId => +filterAIAgentId === +excludeAIAgentId);

                        return isIncluded && !isExcluded;
                    });

                    if (output[field].include.length < 1) {
                        errorTypes.push(field as monitoringFiltersEnum);
                    }
                } else {
                    output[field] = filters[field];
                }
            }

            // The Agents filter narrows an agent by excluding what was unticked under it
            if (filters[field]?.exclude?.length) {
                output[field] = {
                    include: output[field]?.include ?? [],
                    exclude: [...new Set([...(output[field]?.exclude ?? []), ...filters[field].exclude])]
                };
            }
        });

        // No need to merge, this can only be in the filters (for now)
        if (filters.includedProminenceFilter?.length) {
            output.includedProminenceFilter = filters.includedProminenceFilter;
            output.excludedProminenceFilter = [];
        } else if (filters.excludedProminenceFilter?.length) {
            output.excludedProminenceFilter = filters.excludedProminenceFilter;
            output.includedProminenceFilter = [];
        }

        if(filters.discoverListFilter) {
            output.discoverListFilter = new DiscoverListFilterModel({
                discoverList: filters.discoverListFilter.discoverList,
                discoverListType: filters.discoverListFilter.discoverListType,
                useEmploymentOutletId: filters.discoverListFilter.useEmploymentOutletId,
            });
        }

        if (errorTypes.length > 0) {
            throw new InvalidFiltersError('Filters can not be safely applied to search', errorTypes);
        }
        return output;
    }

    /**
     * Gets All of the search keywords from AND and OR categories across all groups
     */
    public get combinedIncludeKeywords(): string[] {
        const searchTermsByKeywordGroup: string[][] = this.combinedIncludeKeywordsByKeywordGroup;

        return flatten(searchTermsByKeywordGroup);
    }

    /**
     * Parse the EBQ and find the key terms that will be used to direct highlighting.
     *
     * Note, currently this logic is pretty basic - it removes any Boolean keywords - and leaves the rest as
     * highlightable terms. The logic of the query is not processed, so terms in the NOT logic will be highlighted
     * if found in the returned search results.
     */
    public getSearchKeywordsFromBooleanQuery(): string[] {
        let searchTerms: string[] = [];
        if (this.expertBooleanQuery !== null &&
            this.expertBooleanQuery.filter.length > 0
        ) {
            const searchString: string = this.expertBooleanQuery.filter.replace(/\b(AND|OR|NOT)\b/g, ':tok:');
            searchTerms = searchString.split(':tok:').filter((element) => element.trim() !== '');
        }

        // need to filter out empty strings
        return searchTerms.map((s) => {
            s = s.trim();
            s = s.replace(/["()]/g, '');

            return s;
        }).filter((element) => element !== '');
    }

    /**
     * Gets All of the search keywords from AND and OR categories for each group
     */
    private get combinedIncludeKeywordsByKeywordGroup(): string[][] {
        let searchTermsByKeywordGroup: string[][];

        if (!this.isExpertSearch()) {
            searchTermsByKeywordGroup = this.keywordGroups.map(
                keywordGroup => keywordGroup.allIncludeKeywords
            );
        }

        return searchTermsByKeywordGroup;
    }

    /**
     * Determines whether or not any
     * keyword groups exist on this query
     */
    private get hasAnyKeywordGroups(): boolean {
        const keywordGroupSearchMode = !this.isExpertSearch();
        const hasKeywordGroups: boolean = keywordGroupSearchMode && Array.isArray(this.keywordGroups);
        return hasKeywordGroups && this.keywordGroups.length > 0;
    }

    /**
     * Determines if there are any keyword groups that have AND or OR, not just NOT
     */
    public hasPositiveKeywordGroups(): boolean {
        for (const keywordGroup of this.keywordGroups) {
            if (keywordGroup.and?.length || keywordGroup.or?.length) {
                return true;
            }
        }
        return false;
    }

    /**
     * Checks to see if all Keyword Groups in a query are exactly the same
     */
    public areAllKeywordGroupsTheSame(): boolean {
        // If no KG or only 1 KG in the query, we pass this test
        if (!this.hasAnyKeywordGroups ||
            this.keywordGroups.length <= 1
        ) {
            return true;
        }

        // We use the first KG as the control and compare the others, in sequence against it
        const maxIndex = this.keywordGroups.length - 1;
        for (let i = 1; i <= maxIndex; i++) {
            if (!this.keywordGroups[0].equals(this.keywordGroups[i])) {
                return false;
            }
        }

        return true;
    }

    /**
     * Converts this front end model to the backend equivalent (SearchCriteriaQueryModel)
     * @param forFilterQuery if the query is used as filter rather than base, some default values would need to adjust
     */
    public getApiEntity(forFilterQuery: boolean = false, isCombinedQuery?: boolean): SearchCriteriaQueryModel {
        const searchCriteriaQuery = new SearchCriteriaQueryModel();

        searchCriteriaQuery.caseSensitive = this.caseSensitive;
        searchCriteriaQuery.sentiment = this.mapSentimentToRanges(this.sentiments, forFilterQuery);

        // The boolean query to be run is either stored in the keyword groups or the is an expert raw boolean query
        if (this.isExpertSearch()) {
            if (forFilterQuery) {
                searchCriteriaQuery.advancedBooleanExpressionFilter = null;
            }
            if (this.expertBooleanQuery?.filter) {
                searchCriteriaQuery.advancedBooleanExpressionFilter = {
                    areas: this.expertBooleanQuery.searchContexts.map(area => searchContextToApiMap.get(area.value)),
                    filter: this.expertBooleanQuery.filter
                } as AdvancedBooleanExpressionInterface;
            }
            delete searchCriteriaQuery.keywordGroups;
        } else {
            searchCriteriaQuery.keywordGroups = this.keywordGroups?.map(keywordGroup => keywordGroup.toApiEntity());
            delete searchCriteriaQuery.advancedBooleanExpressionFilter;
        }

        if (this.isExpertSearch() && isCombinedQuery) {
            searchCriteriaQuery.keywordGroups = this.keywordGroups?.map(keywordGroup => keywordGroup.toApiEntity());
        }

        if (this.drillDownFilters?.length > 0) {
            searchCriteriaQuery.drillDownTermFilters = this.drillDownFilters.map(x => {
                const expertFilters = x.filterAdvancedBooleanExpressionFilters;
                return {
                    filterAdvancedBooleanExpressionFilters: expertFilters?.map(y => {
                        return {
                            areas: y.searchContexts.map(area => searchContextToApiMap.get(area.value)),
                            filter: y.filter
                        } as AdvancedBooleanExpressionInterface;
                    }),
                    filterKeywordGroups: x.filterKeywordGroups?.map(y => y.map(z => z.toApiEntity()))
                } as DrillDownTermFilterApi;
            });
        } else {
            searchCriteriaQuery.drillDownTermFilters = [];
        }
        if (searchCriteriaQuery.drillDownTermFilters.some(x => x.filterAdvancedBooleanExpressionFilters?.length > 0 && !this.expertBooleanQuery?.filter)) {
            searchCriteriaQuery.advancedBooleanExpressionFilter = null;
        }

        searchCriteriaQuery.language = {
            include: this.includedLanguages.map(language => language.id),
            exclude: this.excludedLanguages.map(language => language.id)
        };

        // handle when included or excluded regions has no value, but the raw data is available
        const regionsAreStillRaw = (
            (this.includedRegions.length === 0 && !MentionSearchQueryModel.isRawRegionEmpty(this.rawIncludedRegions))
            || (this.excludedRegions.length === 0 && !MentionSearchQueryModel.isRawRegionEmpty(this.rawExcludedRegions))
        );

        if (regionsAreStillRaw) {
            searchCriteriaQuery.location = {
                include: this.rawIncludedRegions,
                exclude: this.rawExcludedRegions
            };
        } else {
            searchCriteriaQuery.location = {
                include: MentionSearchQueryModel.extractRegions(this.includedRegions),
                exclude: MentionSearchQueryModel.extractRegions(this.excludedRegions)
            };
        }

        searchCriteriaQuery.contact = {
            include: this.parseSources(this.includedSources, SearchCriteriaSourcesEnum.journalist),
            exclude: this.parseSources(this.excludedSources, SearchCriteriaSourcesEnum.journalist)
        };

        searchCriteriaQuery.outlet = {
            include: this.parseSources(this.includedSources, SearchCriteriaSourcesEnum.outlet),
            exclude: this.parseSources(this.excludedSources, SearchCriteriaSourcesEnum.outlet)
        };
        searchCriteriaQuery.url = {
            include: this.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.urls).map(source => source.entityName),
            exclude: this.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.urls).map(source => source.entityName)
        };

        if (this.filterSources?.length) {
            if (forFilterQuery) {
                this.filterSources.forEach(element => {
                    switch (element.type) {
                        case SourceTypeEnum.outlet:
                            searchCriteriaQuery.outlet.include.push(element.value);
                            break;
                        case SourceTypeEnum.journalist:
                            searchCriteriaQuery.contact.include.push(element.value);
                            break;
                        case SourceTypeEnum.url:
                            searchCriteriaQuery.url.include.push(element.value.name);
                            break;
                    }
                });
            } else {
                searchCriteriaQuery.filterSources = this.filterSources.map(filterSource => filterSource.toApiEntity());
            }
        }

        searchCriteriaQuery.mediaTypeFilter = {
            include: this.includedMediaTypes?.map(mediaType => mediaType.id) || [],
            exclude: this.excludedMediaTypes?.map(mediaType => mediaType.id) || []
        };

        searchCriteriaQuery.tweetTypeFilter = {
            include: this.includedTweetTypes?.map(type => type.childId as TwitterSubTypeEnum) || [],
            exclude: this.excludedTweetTypes?.map(type => type.childId as TwitterSubTypeEnum) || []
        };

        searchCriteriaQuery.linkedinTypeFilter = {
            include: this.includedLinkedinTypes?.map(type => type.childId as LinkedinSubTypeEnum) || [],
            exclude: this.excludedLinkedinTypes?.map(type => type.childId as LinkedinSubTypeEnum) || []
        };

        searchCriteriaQuery.blueskyTypeFilter = {
            include: this.includedBlueskyTypes?.map(type => type.childId as BlueskySubTypeEnum) || [],
            exclude: this.excludedBlueskyTypes?.map(type => type.childId as BlueskySubTypeEnum) || []
        };

        searchCriteriaQuery.tvTypeFilter = {
            include: this.includedTvTypes?.map(type => type.childId as BroadcastSubTypeEnum) || [],
            exclude: this.excludedTvTypes?.map(type => type.childId as BroadcastSubTypeEnum) || []
        };

        if (this.isArchivedMention !== undefined && this.isArchivedMention !== null) {
            searchCriteriaQuery.isArchivedMention = this.isArchivedMention;
        }

        if (this.dateQuery) {
            let apiDate: SearchCriteriaDateQueryModel = {
                relativeStartOffsetH: this.dateQuery.relativeStartOffsetH,
                relativeEndOffsetH: this.dateQuery.relativeEndOffsetH,
                dateType: this.dateQuery.type
            } as SearchCriteriaDateQueryModel;

            if (this.dateQuery.absoluteRange) {
                apiDate.dateRange = {
                    start: this.dateQuery.absoluteRange.start.getTime(),
                    end: this.dateQuery.absoluteRange.end.getTime()
                };
            } else if (this.dateQuery.absoluteDate) {
                apiDate.dateRange = {
                    start: moment(this.dateQuery.absoluteDate).startOf('day').toDate().getTime(),
                    end: moment(this.dateQuery.absoluteDate).endOf('day').toDate().getTime()
                };
            }

            if (this.dateQuery.relativeTimezoneDateType) {
                apiDate.relativeTimezoneDateType = this.dateQuery.relativeTimezoneDateType;
            }

            if (this.dateQuery.timezone) {
                apiDate.timeZone = this.dateQuery.timezone;
            }

            if (this.dateQuery.type === DateQueryTypeEnum.ALL || !this.dateQuery.type) {
                apiDate = null;
            }

            if (this.dateQuery.dateType === DateQueryDateTypeEnum.HARVESTED) {
                searchCriteriaQuery.harvestedDate = apiDate;
            } else if (this.dateQuery.dateType === DateQueryDateTypeEnum.SAVED) {
                searchCriteriaQuery.savedDate = apiDate;
            } else if (this.dateQuery.dateType === DateQueryDateTypeEnum.EDITED) {
                searchCriteriaQuery.editedDate = apiDate;
            } else {
                searchCriteriaQuery.publicationDate = apiDate;
            }
        }

        if (Number.isFinite(this.ave.min)) {
            searchCriteriaQuery.adValueUsd = {
                min: this.ave.min,
                max: Number.isFinite(this.ave.max) ? this.ave.max : Mention.maxAVE
            };
        } else if (Number.isFinite(this.ave.max)) {
            searchCriteriaQuery.adValueUsd = {
                min: null,
                max: this.ave.max
            };
        }
        if (Number.isFinite(this.audienceReach.min)) {
            searchCriteriaQuery.audienceReach  = {
                min: this.audienceReach.min,
                max: Number.isFinite(this.audienceReach.max) ? this.audienceReach.max : Mention.maxAudienceReach
            };
        } else if (Number.isFinite(this.audienceReach.max)) {
            searchCriteriaQuery.audienceReach  = {
                min: null,
                max: this.audienceReach.max
            };
        }

        engagementFilterApiKeys.forEach(key => {
            const value = this.engagementFilter[key];
            if (value && (!isNaN(value.min) || !isNaN(value.max))) {
                searchCriteriaQuery.engagementFilter[key] = {
                    min: !isNaN(value.min) ? value.min : 0,
                    max: !isNaN(value.max) ? value.max : (key === 'engagementRate' ? Mention.maxEngagementRate : Mention.maxEngagementTotal)
                };
            } else {
                // If this.engagementFilter[key] is not explicitly set, we need to remove it in order to include all possible results
                delete searchCriteriaQuery.engagementFilter[key];
            }
        });

        // Minimum domain authority score is 1
        if (!isNaN(this.domainAuthority.min) || !isNaN(this.domainAuthority.max)) {
            searchCriteriaQuery.domainAuthority = {
                min: !isNaN(this.domainAuthority.min) ? this.domainAuthority.min : 1,
                max: !isNaN(this.domainAuthority.max) ? this.domainAuthority.max : Mention.maxDomainAuthorityScore
            };
        }

        if (this.folder) {
            searchCriteriaQuery.folder = {
                include: this.folder.include ? this.folder.include.slice() : [],
                exclude: this.folder.exclude ? this.folder.exclude.slice() : []
            };
        } else {
            searchCriteriaQuery.folder = {
                include: [],
                exclude: []
            };
        }

        if (this.tag) {
            searchCriteriaQuery.tag = {
                include: this.tag.include ? this.tag.include.slice() : [],
                exclude: this.tag.exclude ? this.tag.exclude.slice() : []
            };
        } else {
            searchCriteriaQuery.tag = {
                include: [],
                exclude: []
            };
        }

        if (this.agent) {
            searchCriteriaQuery.agent = {
                include: this.agent.include ? this.agent.include.slice() : [],
                exclude: this.agent.exclude ? this.agent.exclude.slice() : []
            };
            if (forFilterQuery && searchCriteriaQuery.agent.include.length === 0 && searchCriteriaQuery.agent.exclude.length === 0) {
                searchCriteriaQuery.agent = null;
            }
        } else {
            if (forFilterQuery) {
                searchCriteriaQuery.agent = null;
            } else {
                searchCriteriaQuery.agent = {
                    include: [],
                    exclude: []
                };
            }
        }

        // Agent attribution: only sent when set, so an ordinary search is unaffected by them
        [
            'aiAgent',
            'aiAgentSource',
            'aiAgentAdhoc'
        ].forEach((field: 'aiAgent' | 'aiAgentSource' | 'aiAgentAdhoc') => {
            const filter = this[field];

            searchCriteriaQuery[field] = filter?.include?.length || filter?.exclude?.length
                ? {
                    include: filter.include ? filter.include.slice() : [],
                    exclude: filter.exclude ? filter.exclude.slice() : []
                } : {
                    include: [],
                    exclude: []
                };
        });

        if (this.subscription) {
            searchCriteriaQuery.subscription = {
                include: this.subscription.include ? this.subscription.include.slice() : [],
                exclude: this.subscription.exclude ? this.subscription.exclude.slice() : []
            };
        } else {
            searchCriteriaQuery.subscription = {
                include: [],
                exclude: []
            };
        }

        // b/e doesn't allow filtering of both agent and theme, only add theme if agent is empty
        if (this.theme && this.agent?.include.length === 0) {
            searchCriteriaQuery.theme = {
                include: this.theme.include ? this.theme.include.slice() : [],
                exclude: this.theme.exclude ? this.theme.exclude.slice() : []
            };
        } else {
            searchCriteriaQuery.theme = {
                include: [],
                exclude: []
            };
        }

        if (this.aiAgent) {
            searchCriteriaQuery.aiAgent = {
                include: this.aiAgent.include ? this.aiAgent.include.slice() : [],
                exclude: this.aiAgent.exclude ? this.aiAgent.exclude.slice() : []
            };
        } else {
            searchCriteriaQuery.aiAgent = {
                include: [],
                exclude: []
            };
        }

        if (this.aiAgentSource) {
            searchCriteriaQuery.aiAgentSource = {
                include: this.aiAgentSource.include ? this.aiAgentSource.include.slice() : [],
                exclude: this.aiAgentSource.exclude ? this.aiAgentSource.exclude.slice() : []
            };
        } else {
            searchCriteriaQuery.aiAgentSource = {
                include: [],
                exclude: []
            };
        }

        if (this.aiAgentAdhoc) {
            searchCriteriaQuery.aiAgentAdhoc = {
                include: this.aiAgentAdhoc.include ? this.aiAgentAdhoc.include.slice() : [],
                exclude: this.aiAgentAdhoc.exclude ? this.aiAgentAdhoc.exclude.slice() : []
            };
        } else {
            searchCriteriaQuery.aiAgentAdhoc = {
                include: [],
                exclude: []
            };
        }

        if (this.tagGroup) {
            searchCriteriaQuery.tagGroup = {
                include: this.tagGroup.include ? this.tagGroup.include.slice() : [],
                exclude: this.tagGroup.exclude ? this.tagGroup.exclude.slice() : []
            };
        } else {
            searchCriteriaQuery.tagGroup = {
                include: [],
                exclude: []
            };
        }

        if (this.hashtags) {
            searchCriteriaQuery.hashtagFilter = {
                include: this.hashtags.include ? this.hashtags.include.slice() : [],
                exclude: this.hashtags.exclude ? this.hashtags.exclude.slice() : []
            };
        }

        searchCriteriaQuery.sourceGroup = {
            include: this.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.sourceGroups).map(source => +source.entityName),
            exclude: this.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.sourceGroups).map(source => +source.entityName)
        };

        if (this.sourceGroup && (this.sourceGroup.include.length > 0 || this.sourceGroup.exclude.length > 0)) {
            searchCriteriaQuery.sourceGroup = {
                include: this.sourceGroup.include,
                exclude: this.sourceGroup.exclude
            };
        }

        if (this.includedProminenceFilter?.length) {
            searchCriteriaQuery.prominenceBasicFilter.include = this.includedProminenceFilter;
        } else if (this.excludedProminenceFilter?.length) {
            searchCriteriaQuery.prominenceBasicFilter.exclude = this.excludedProminenceFilter;
        }
        searchCriteriaQuery.agentId = this.agentId;
        searchCriteriaQuery.themeId = this.themeId;
        searchCriteriaQuery.enableEngagementInsights = this.enableEngagementInsights;
        if (this.socialSearchRequest) {
            searchCriteriaQuery.socialSearchRequest = this.socialSearchRequest;
        }

        if (this.discoverListFilter) {
            searchCriteriaQuery.discoverListFilter = this.discoverListFilter.toApiModel();
        }

        if (this.sampleFilter) {
            searchCriteriaQuery.sampleFilter = cloneDeep(this.sampleFilter);
        }

        if (this.syndicateIds) {
            searchCriteriaQuery.syndicateIds = {
                include: this.syndicateIds.include ? this.syndicateIds.include.slice() : [],
                exclude: this.syndicateIds.exclude ? this.syndicateIds.exclude.slice() : []
            };
        }

        return searchCriteriaQuery;
    }

    /**
     * Return true if expert query, false if standard (keyword group query)
     */
    isExpertSearch(): boolean {
        return this.searchMode === SearchMode.expert;
    }

    /**
     * Determines whether or not this query has any
     * keyword groups with the provided search context
     */
    public hasMatchingSearchContext(searchContext: SearchContextsEnum): boolean {
        const matchingKeywordGroups = this.getKeywordGroupsBySearchContext(searchContext);
        const matchingExpertBoolean = this.expertBooleanQuery.searchContexts.find(searchContextModel => searchContextModel.value === searchContext);

        return matchingKeywordGroups.length > 0 || !!matchingExpertBoolean ;
    }

    /**
     * Constructs the model from the api entity
     */
    private constructFromApiEntity(apiEntity: SearchCriteriaQueryModel, isCombinedQuery?: boolean): void {
        this.caseSensitive = apiEntity.caseSensitive;

        // The search query is either added to keyword groups or to the expert raw search query
        if (apiEntity.keywordGroups) {
            this.keywordGroups = apiEntity.keywordGroups.map(group => new MonitoringKeywordGroupModel(group));
            this.searchMode = SearchMode.standard;
            this.expertBooleanQuery = new MonitoringAdvancedBooleanModel();
        }
        if (apiEntity.advancedBooleanExpressionFilter && apiEntity.advancedBooleanExpressionFilter.filter) {
            this.expertBooleanQuery = new MonitoringAdvancedBooleanModel(apiEntity.advancedBooleanExpressionFilter);
            this.searchMode = SearchMode.expert;
            if (!isCombinedQuery) {
                this.keywordGroups = [];
            }
        }

        if (apiEntity.drillDownTermFilters?.length > 0) {
            this.drillDownFilters = apiEntity.drillDownTermFilters.map(x => {
                const drillDownFilter = {} as DrillDownTermFilter;
                if (x.filterAdvancedBooleanExpressionFilters?.length > 0) {
                    drillDownFilter.filterAdvancedBooleanExpressionFilters =
                        x.filterAdvancedBooleanExpressionFilters.map(y => new MonitoringAdvancedBooleanModel(y));
                }
                if (x.filterKeywordGroups?.length > 0) {
                    drillDownFilter.filterKeywordGroups = x.filterKeywordGroups.map(y => y.map(z => new MonitoringKeywordGroupModel(z)));
                }

                return drillDownFilter;
            });
        }

        if (apiEntity.language) {
            this.includedLanguages = apiEntity.language.include.map(
                language => new LanguageModel(language)
            );

            this.excludedLanguages = apiEntity.language.exclude.map(
                language => new LanguageModel(language)
            );
        } else {
            this.includedLanguages = [];
            this.excludedLanguages = [];
        }

        if (apiEntity.filterSources) {
            this.filterSources = apiEntity.filterSources.map(source => new FilterSourceModel(source));
        } else {
            this.filterSources = [];
        }

        this.includedSources = [];
        this.excludedSources = [];

        /**
         * @todo move outlets and contacts strings to use localization
         */
        if (apiEntity.outlet) {
            if (apiEntity.outlet.include) {
                this.mapSources(
                    this.includedSources,
                    apiEntity.outlet.include,
                    SearchCriteriaSourcesEnum.outlet
                );
            }
            if (apiEntity.outlet.exclude) {
                this.mapSources(
                    this.excludedSources,
                    apiEntity.outlet.exclude,
                    SearchCriteriaSourcesEnum.outlet
                );
            }
        }

        if (apiEntity.contact) {
            if (apiEntity.contact.include) {
                this.mapSources(
                    this.includedSources,
                    apiEntity.contact.include,
                    SearchCriteriaSourcesEnum.journalist
                );
            }
            if (apiEntity.contact.exclude) {
                this.mapSources(
                    this.excludedSources,
                    apiEntity.contact.exclude,
                    SearchCriteriaSourcesEnum.journalist
                );
            }
        }

        if (apiEntity.url) {
            if (apiEntity.url.include) {
                this.mapUrlSources(this.includedSources,
                    apiEntity.url.include
                );
            }
            if (apiEntity.url.exclude) {
                this.mapUrlSources(this.excludedSources,
                    apiEntity.url.exclude
                );
            }
        }

        if (apiEntity.sourceGroup) {
            this.sourceGroup = {
                include: apiEntity.sourceGroup?.include || [],
                exclude: apiEntity.sourceGroup?.exclude || []
            };
            if (apiEntity.sourceGroup.include) {
                this.mapSourceGroupSources(
                    this.includedSources,
                    apiEntity.sourceGroup?.include
                );
            }
            if (apiEntity.sourceGroup.exclude) {
                this.mapSourceGroupSources(
                    this.excludedSources,
                    apiEntity.sourceGroup?.exclude
                );
            }
        } else {
            this.sourceGroup = {
                include: [],
                exclude: []
            };
        }

        // We store the returned region information as-is since we won't fetch the addtional Journodex
        // geographic data we need until later, as we need to make API calls to Journodex
        this.rawExcludedRegions = null;
        this.rawIncludedRegions = null;
        this.includedRegions = [];
        this.excludedRegions = [];
        if (apiEntity.location) {
            if (apiEntity.location.include) {
                this.rawIncludedRegions = this.convertRegionIdsToNumbers(apiEntity.location.include);
            }
            if (apiEntity.location.exclude) {
                this.rawExcludedRegions = this.convertRegionIdsToNumbers(apiEntity.location.exclude);
            }
        }

        this.includedMediaTypes = apiEntity.mediaTypeFilter?.include.map(mediaTypeId => new MediaTypeModel(mediaTypeId));
        // mediaTypes is replaced by mediaTypeFilter, here we deal with old data
        if (apiEntity.mediaTypes?.length) {
            this.includedMediaTypes = apiEntity.mediaTypes.map(mediaTypeId => new MediaTypeModel(mediaTypeId));
        }
        this.excludedMediaTypes = apiEntity.mediaTypeFilter?.exclude.map(mediaTypeId => new MediaTypeModel(mediaTypeId));

        this.includedTweetTypes = apiEntity.tweetTypeFilter?.include.map(type => new MediaTypeModel(MediaTypeEnum.TWITTER, type));
        this.excludedTweetTypes = apiEntity.tweetTypeFilter?.exclude.map(type => new MediaTypeModel(MediaTypeEnum.TWITTER, type));

        this.includedLinkedinTypes = apiEntity.linkedinTypeFilter?.include ?
            apiEntity.linkedinTypeFilter?.include.map(type => new MediaTypeModel(MediaTypeEnum.LINKEDIN, type)) : [];
        this.excludedLinkedinTypes = apiEntity.linkedinTypeFilter?.exclude ?
            apiEntity.linkedinTypeFilter?.exclude.map(type => new MediaTypeModel(MediaTypeEnum.LINKEDIN, type)) : [];

        this.includedBlueskyTypes = apiEntity.blueskyTypeFilter?.include ?
            apiEntity.blueskyTypeFilter?.include.map(type => new MediaTypeModel(MediaTypeEnum.BLUESKY, type)) : [];
        this.excludedBlueskyTypes = apiEntity.blueskyTypeFilter?.exclude ?
            apiEntity.blueskyTypeFilter?.exclude.map(type => new MediaTypeModel(MediaTypeEnum.BLUESKY, type)) : [];

        this.includedTvTypes = apiEntity.tvTypeFilter?.include?.map(type => new MediaTypeModel(MediaTypeEnum.TV, type)) ?? [];
        this.excludedTvTypes = apiEntity.tvTypeFilter?.exclude?.map(type => new MediaTypeModel(MediaTypeEnum.TV, type)) ?? [];

        if (apiEntity.isArchivedMention !== null && apiEntity.isArchivedMention !== undefined) {
            this.isArchivedMention = apiEntity.isArchivedMention;
        }

        if (apiEntity.syndicateIds) {
            this.syndicateIds = {
                include: apiEntity.syndicateIds.include ? apiEntity.syndicateIds.include.slice() : [],
                exclude: apiEntity.syndicateIds.exclude ? apiEntity.syndicateIds.exclude.slice() : []
            };
        }

        this.mapSentimentsFromRanges(apiEntity.sentiment);

        const publicationDate: SearchCriteriaDateQueryModel = apiEntity.publicationDate as SearchCriteriaDateQueryModel;
        const harvestedDate: SearchCriteriaDateQueryModel = apiEntity.harvestedDate as SearchCriteriaDateQueryModel;
        const savedDate: SearchCriteriaDateQueryModel = apiEntity.savedDate as SearchCriteriaDateQueryModel;
        const editedDate: SearchCriteriaDateQueryModel = apiEntity.editedDate as SearchCriteriaDateQueryModel;

        if (publicationDate) {
            this.dateQuery = this.constructDateQueryFromApiEntity(publicationDate);
            this.dateQuery.dateType = DateQueryDateTypeEnum.PUBLISHED;
        } else if (harvestedDate) {
            this.dateQuery = this.constructDateQueryFromApiEntity(harvestedDate);
            this.dateQuery.dateType = DateQueryDateTypeEnum.HARVESTED;
        } else if (savedDate) {
            this.dateQuery = this.constructDateQueryFromApiEntity(savedDate);
            this.dateQuery.dateType = DateQueryDateTypeEnum.SAVED;
        } else if (editedDate) {
            this.dateQuery = this.constructDateQueryFromApiEntity(editedDate);
            this.dateQuery.dateType = DateQueryDateTypeEnum.EDITED;
        } else {
            this.dateQuery = this.constructDateQueryFromApiEntity(null);
            this.dateQuery.dateType = undefined;
        }

        this.ave = {};
        if (apiEntity.adValueUsd) {
            if (!isNaN(apiEntity.adValueUsd.min)) {
                this.ave.min = apiEntity.adValueUsd.min;
            }
            if (!isNaN(apiEntity.adValueUsd.max)) {
                this.ave.max = apiEntity.adValueUsd.max;
            }
        }
        this.audienceReach = {};
        if (apiEntity.audienceReach) {
            if (!isNaN(apiEntity.audienceReach.min)) {
                this.audienceReach.min = apiEntity.audienceReach.min;
            }
            if (!isNaN(apiEntity.audienceReach.max)) {
                this.audienceReach.max = apiEntity.audienceReach.max;
            }
        }
        this.domainAuthority = {};
        if (apiEntity.domainAuthority) {
            if (!isNaN(apiEntity.domainAuthority.min)) {
                this.domainAuthority.min = apiEntity.domainAuthority.min;
            }
            if (!isNaN(apiEntity.domainAuthority.max)) {
                this.domainAuthority.max = apiEntity.domainAuthority.max;
            }
        }
        if (apiEntity.folder) {
            this.folder = {
                include: apiEntity.folder.include || [],
                exclude: apiEntity.folder.exclude || []
            };
        } else {
            this.folder = {
                include: [],
                exclude: []
            };
        }
        if (apiEntity.tag) {
            this.tag = {
                include: apiEntity.tag.include || [],
                exclude: apiEntity.tag.exclude || []
            };
        } else {
            this.tag = {
                include: [],
                exclude: []
            };
        }

        if (apiEntity.agent) {
            this.agent = {
                include: apiEntity.agent.include || [],
                exclude: apiEntity.agent.exclude || []
            };
        } else {
            this.agent = {
                include: [],
                exclude: []
            };
        }

        // Agent attribution, read back so it survives a round trip through the api entity - the
        // agent feed sets these once and the search service rebuilds the query from the entity
        [
            'aiAgent',
            'aiAgentSource',
            'aiAgentAdhoc'
        ].forEach((field: 'aiAgent' | 'aiAgentSource' | 'aiAgentAdhoc') => {
            const filter = apiEntity[field];

            this[field] = {
                include: filter?.include || [],
                exclude: filter?.exclude || []
            };
        });

        if (apiEntity.subscription) {
            this.subscription = {
                include: apiEntity.subscription.include || [],
                exclude: apiEntity.subscription.exclude || []
            };
        } else {
            this.subscription = {
                include: [],
                exclude: []
            };
        }

        if (apiEntity.theme) {
            this.theme = {
                include: apiEntity.theme.include || [],
                exclude: apiEntity.theme.exclude || []
            };
        } else {
            this.theme = {
                include: [],
                exclude: []
            };
        }

        if (apiEntity.aiAgent) {
            this.aiAgent = {
                include: apiEntity.aiAgent.include || [],
                exclude: apiEntity.aiAgent.exclude || []
            };
        } else {
            this.aiAgent = {
                include: [],
                exclude: []
            };
        }

        if (apiEntity.aiAgentSource) {
            this.aiAgentSource = {
                include: apiEntity.aiAgentSource.include || [],
                exclude: apiEntity.aiAgentSource.exclude || []
            };
        } else {
            this.aiAgentSource = {
                include: [],
                exclude: []
            };
        }

        if (apiEntity.aiAgentAdhoc) {
            this.aiAgentAdhoc = {
                include: apiEntity.aiAgentAdhoc.include || [],
                exclude: apiEntity.aiAgentAdhoc.exclude || []
            };
        } else {
            this.aiAgentAdhoc = {
                include: [],
                exclude: []
            };
        }

        if (apiEntity.tagGroup) {
            this.tagGroup = {
                include: apiEntity.tagGroup.include || [],
                exclude: apiEntity.tagGroup.exclude || []
            };
        } else {
            this.tagGroup = {
                include: [],
                exclude: []
            };
        }

        if (apiEntity.hashtags?.length) {
            // deal with old data
            this.hashtags = {
                include: apiEntity.hashtags,
                exclude:  []
            };
        } else if (apiEntity.hashtagFilter) {
            this.hashtags = {
                include: apiEntity.hashtagFilter.include || [],
                exclude: apiEntity.hashtagFilter.exclude || []
            };
        }

        engagementFilterApiKeys.forEach(key => {
            const apiValue = apiEntity.engagementFilter?.[key];
            if (apiValue) {
                const value: RangeModel = { min: NaN, max: NaN };
                value.min = apiValue.min > 0 ? apiValue.min : NaN;
                value.max = apiValue.max < (key === 'engagementRate' ? Mention.maxEngagementRate : Mention.maxEngagementTotal)
                    ? apiValue.max
                    : NaN;
                this.engagementFilter[key] = value;
            }
        });

        if (apiEntity.prominenceFilter?.filters?.length) {
            // backward compatibility
            this.includedProminenceFilter = apiEntity.prominenceFilter?.filters;
        } else if (apiEntity.prominenceBasicFilter?.include?.length) {
            this.includedProminenceFilter = apiEntity.prominenceBasicFilter.include;
            this.excludedProminenceFilter = [];
        } else if (apiEntity.prominenceBasicFilter?.exclude?.length) {
            this.excludedProminenceFilter = apiEntity.prominenceBasicFilter.exclude;
            this.includedProminenceFilter = [];
        }

        this.agentId = apiEntity.agentId;
        this.themeId = apiEntity.themeId;
        this.enableEngagementInsights = apiEntity.enableEngagementInsights;
        if (apiEntity.socialSearchRequest) {
            this.socialSearchRequest = apiEntity.socialSearchRequest;
        }
        if (apiEntity.discoverListFilter) {
            this.discoverListFilter = new DiscoverListFilterModel(apiEntity.discoverListFilter);
        } else {
            this.discoverListFilter = null;
        }

        if (apiEntity.sampleFilter) {
            this.sampleFilter = new SampleFilterModel(apiEntity.sampleFilter);
        }

    }

    private convertRegionIdsToNumbers(regions: GeographyQueryModel): GeographyQueryModel {
        return {
            cities: regions.cities ? regions.cities.map(id => +id) : [],
            continents: regions.continents ? regions.continents.map(id => +id) : [],
            counties: regions.counties ? regions.counties.map(id => +id) : [],
            countries: regions.countries ? regions.countries.map(id => +id) : [],
            provinces: regions.provinces ? regions.provinces.map(id => +id) : []
        };
    }

    private constructDateQueryFromApiEntity(apiEntity: SearchCriteriaDateQueryModel): DateQueryModel {
        if (!apiEntity) {
            apiEntity = new SearchCriteriaDateQueryModel();
        }

        const hasDateRange: boolean = apiEntity.dateRange !== null && typeof apiEntity.dateRange === 'object';
        const dateQuery: DateQueryModel = {
            type: apiEntity.dateType || DateQueryTypeEnum.ALL,
            relativeStartOffsetH: apiEntity.relativeStartOffsetH,
            relativeEndOffsetH: apiEntity.relativeEndOffsetH,
            absoluteDate: apiEntity.absoluteDate,
            relativeTimezoneDateType: apiEntity.relativeTimezoneDateType,
            timezone: apiEntity.timeZone
        };

        if (hasDateRange) {
            const dateRangeEntity: DateRangeModel = apiEntity.dateRange;
            const startTimestamp: number = dateRangeEntity.start;
            const endTimestamp: number = dateRangeEntity.end;
            const hasStartTimestamp: boolean = Number.isInteger(startTimestamp);
            const hasEndTimestamp: boolean = Number.isInteger(endTimestamp);

            if (hasStartTimestamp && hasEndTimestamp) {
                dateQuery.absoluteRange = {
                    start: new Date(dateRangeEntity.start),
                    end: new Date(dateRangeEntity.end)
                };
                dateQuery.relativeStartOffsetH = null;
                dateQuery.relativeEndOffsetH = null;
            }
        }

        return dateQuery;
    }

    /**
     * Constructs the default version of this model
     */
    private constructDefault(): void {
        this.includedMediaTypes = [];
        this.excludedMediaTypes = [];
        this.includedTweetTypes = [];
        this.excludedTweetTypes = [];
        this.includedLinkedinTypes = [];
        this.excludedLinkedinTypes = [];
        this.includedBlueskyTypes = [];
        this.excludedBlueskyTypes = [];
        this.includedTvTypes = [];
        this.excludedTvTypes = [];
        this.sentiments = [];
        this.includedLanguages = [];
        this.excludedLanguages = [];
        this.includedSources = [];
        this.excludedSources = [];
        this.includedRegions = [];
        this.excludedRegions = [];
        this.dateQuery = null;
        this.audienceReach = {};
        this.engagementFilter = {
            engagementRate: {},
            engagementTotal: {},
            amplification: {},
            conversation: {},
            applause: {},
            view: {},
        };
        this.ave = {};
        this.folder = { include: [], exclude: [] };
        this.tag = { include: [], exclude: [] };
        this.agent = { include: [], exclude: [] };
        this.aiAgent = { include: [], exclude: [] };
        this.aiAgentSource = { include: [], exclude: [] };
        this.aiAgentAdhoc = { include: [], exclude: [] };
        this.subscription = { include: [], exclude: [] };
        this.theme = { include: [], exclude: [] };
        this.aiAgent = { include: [], exclude: [] };
        this.aiAgentSource = { include: [], exclude: [] };
        this.aiAgentAdhoc = { include: [], exclude: [] };
        this.tagGroup = { include: [], exclude: [] };
        this.sourceGroup = { include: [], exclude: [] };
        this.hashtags = { include: [], exclude: [] };;
        this.domainAuthority = {};

        this.expertBooleanQuery = new MonitoringAdvancedBooleanModel();
        this.keywordGroups = [new MonitoringKeywordGroupModel()];
        this.searchMode = SearchMode.standard;
        this.caseSensitive = false;
    }

    /**
     * Gets all of the keyword groups with matching search contexts
     */
    private getKeywordGroupsBySearchContext(searchContext: SearchContextsEnum): MonitoringKeywordGroupModel[] {
        const hasAnyKeywordGroups: boolean = this.hasAnyKeywordGroups;
        let keywordGroups: MonitoringKeywordGroupModel[] = [];

        if (hasAnyKeywordGroups) {
            keywordGroups = this.keywordGroups.filter(
                keywordGroup => keywordGroup.hasMatchingSearchContext(searchContext)
            );
        }

        return keywordGroups;
    }

    /**
     * Map API search criteria for ranges to Front End enumeration values
     */
    private mapSentimentsFromRanges(searchCriteriaSentiments: SentimentQueryModel): void {
        this.sentiments = [];
        if (searchCriteriaSentiments) {
            if (searchCriteriaSentiments.includeUnset === true) {
                this.sentiments.push(new SentimentModel(SearchCriteriaQuerySentimentsEnum.notAvailable));
            }
            for (let i = 0; i < searchCriteriaSentiments.ranges.length; i++) {
                if (this.isSentimentSame(
                    searchCriteriaSentiments.ranges[i],
                    MentionSearchQueryModel.negativeSentimentRange())
                ) {
                    this.sentiments.push(new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative));
                } else if (this.isSentimentSame(
                    searchCriteriaSentiments.ranges[i],
                    MentionSearchQueryModel.positiveSentimentRange())
                ) {
                    this.sentiments.push(new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive));
                } else if (this.isSentimentSame(
                    searchCriteriaSentiments.ranges[i],
                    MentionSearchQueryModel.neutralSentimentRange())
                ) {
                    this.sentiments.push(new SentimentModel(SearchCriteriaQuerySentimentsEnum.neutral));
                }
            }
        }
    }

    /**
     * Compares 2 SentimentRangeModel objects and returns boolean value assessing if they represent the same ranges
     *
     * @param sentimentRange1
     * @param sentimentRange2
     */
    private isSentimentSame(sentimentRange1: SentimentRangeModel, sentimentRange2: SentimentRangeModel): boolean {
        return sentimentRange1.max === sentimentRange2.max &&
            sentimentRange1.min === sentimentRange2.min;
    }

    /**
     * Map Front End enums on sentiment values to API model ranges
     */
    private mapSentimentToRanges(sentiments: SentimentModel[], forFilterQuery: boolean = false): SentimentQueryModel {
        let sentimentRange = {
            includeUnset: false,
            ranges: []
        };

        if (sentiments?.length) {
            for (let i = 0; i < sentiments.length; i++) {
                switch (sentiments[i].id) {
                    case SearchCriteriaQuerySentimentsEnum.negative:
                        sentimentRange.ranges.push(MentionSearchQueryModel.negativeSentimentRange());
                        break;
                    case SearchCriteriaQuerySentimentsEnum.neutral:
                        sentimentRange.ranges.push(MentionSearchQueryModel.neutralSentimentRange());
                        break;
                    case SearchCriteriaQuerySentimentsEnum.positive:
                        sentimentRange.ranges.push(MentionSearchQueryModel.positiveSentimentRange());
                        break;
                    case SearchCriteriaQuerySentimentsEnum.notAvailable:
                        sentimentRange.includeUnset = true;
                        break;
                }
            }
        } else if (forFilterQuery) {
            sentimentRange = null;
        }

        return sentimentRange;
    }

    /*
    * Maps Sources from Saved Searches Api into front end model JournodexSourceModel
    */
    private mapSources(
        sourcesObject: JournodexSourceModel[],
        sources: JournodexBasicModel[],
        sourceType: SearchCriteriaSourcesEnum
    ): void {
        const hasSources: boolean = Array.isArray(sources);

        if (hasSources) {
            for (let i = 0; i < sources.length; i++) {
                const journodexSource: JournodexSourceModel = new JournodexSourceModel();
                if (sources[i].id) {
                    journodexSource.id = sources[i].id;
                }
                if (sources[i].name) {
                    const name = sources[i].name.replace(/^"*|"*$/g, '');
                    journodexSource.entityName = name;
                    journodexSource.displayString = name;
                }
                journodexSource.entityType = sourceType;


                sourcesObject.push(journodexSource);
            }
        }
    }

    /**
     * Map url sources from backend into front-end model JournodexSourceModel
     */
    mapUrlSources(sourcesObject: JournodexSourceModel[], sources: string[]) {
        const hasSources: boolean = Array.isArray(sources);

        if (hasSources) {
            for (let i = 0; i < sources.length; i++) {
                const journodexSource: JournodexSourceModel = new JournodexSourceModel();
                const name = sources[i].replace(/^"*|"*$/g, '');
                journodexSource.entityName = name;
                journodexSource.displayString = name;
                journodexSource.entityType = SearchCriteriaSourcesEnum.urls;

                sourcesObject.push(journodexSource);
            }
        }
    }

    /**
     * Map source groups from backend into front-end model JournodexSourceModel
     */
    mapSourceGroupSources(sourcesObject: JournodexSourceModel[], sourceIds: number[]) {
        const hasSources: boolean = Array.isArray(sourceIds);

        if (hasSources) {
            for (const sourceId of sourceIds) {
                const journodexSource: JournodexSourceModel = new JournodexSourceModel();
                const name = sourceId.toString();

                journodexSource.entityName = name;
                journodexSource.entityType = SearchCriteriaSourcesEnum.sourceGroups;

                sourcesObject.push(journodexSource);
            }
        }
    }

    private parseSources(listOfSources: JournodexSourceModel[], targetType: SearchCriteriaSourcesEnum): JournodexBasicModel[] {
        const hasListOfSources: boolean = Array.isArray(listOfSources);
        const parsedSourceList: JournodexBasicModel[] = [];

        if (hasListOfSources) {
            for (let i = 0; i < listOfSources.length; i++) {
                if (listOfSources[i].entityType === targetType) {
                    // Id is only used for "recent coverage" search for targeting profiles
                    // for monitoring searches, the name is preferred
                    if (!listOfSources[i].entityName) {
                        parsedSourceList.push(
                            {
                                id: listOfSources[i].id
                            }
                        );
                    } else {
                        parsedSourceList.push(
                            {
                                // @HACK add quotations internally to make it an exact match
                                name: `"${listOfSources[i].entityName}"`
                            }
                        );
                    }
                }
            }
        }
        return parsedSourceList;
    }

    /**
     * Gets the last keyword group from the keyword groups
     */
    private getLastKeywordGroup(): MonitoringKeywordGroupModel {
        const lastKeywordGroup: MonitoringKeywordGroupModel = this.keywordGroups[this.keywordGroups.length - 1];
        return lastKeywordGroup;
    }

    apiMentionMatchesSearch(mention: MentionModel, aveUserCurrencyValue: number): boolean {
        // NOTE: since mentions often don't have their full bodies, we can't really verify this properly right now
        // if (this.keywordGroups && this.keywordGroups.length > 0) {
        // }

        if (this.includedSources && this.includedSources.length > 0) {
            const includedContacts = this.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);
            const excludedContacts = this.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);

            const includedOutlets = this.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
            const excludedOutlets = this.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);

            if (includedContacts.length > 0) {
                if (!mention.contactName) {
                    return false;
                }
                const foundContact = includedContacts.find(contact => mention.contactName.toLocaleLowerCase().includes(contact.entityName.toLocaleLowerCase()));

                if (!foundContact) {
                    return false;
                }
            } else if (excludedContacts.length) {
                const foundContact = excludedContacts.find(contact => mention.contactName.toLocaleLowerCase().includes(contact.entityName.toLocaleLowerCase()));

                if (foundContact) {
                    return false;
                }
            }

            if (includedOutlets.length > 0) {
                if (!mention.outletName) {
                    return false;
                }
                const foundOutlet = includedOutlets.find(outlet => mention.outletName.toLocaleLowerCase().includes(outlet.entityName.toLocaleLowerCase()));

                if (!foundOutlet) {
                    return false;
                }
            } else if (excludedOutlets.length) {
                const foundOutlet = excludedOutlets.find(outlet => mention.outletName.toLocaleLowerCase().includes(outlet.entityName.toLocaleLowerCase()));

                if (foundOutlet) {
                    return false;
                }
            }
        }

        if (this.includedLanguages && this.includedLanguages.length > 0) {
            const foundLanguage = this.includedLanguages.find(lang => lang.id === mention.language);

            if (!foundLanguage) {
                return false;
            }
        } else if (this.excludedLanguages?.length) {
            const foundLanguage = this.excludedLanguages.find(lang => lang.id === mention.language);

            if (foundLanguage) {
                return false;
            }
        }

        let rawIncludeRegions: GeographyQueryModel = null;

        if (!MentionSearchQueryModel.isRawRegionEmpty(this.rawIncludedRegions)) {
            rawIncludeRegions = this.rawIncludedRegions;
        } else if (this.includedRegions && this.includedRegions.length > 0) {
            rawIncludeRegions = MentionSearchQueryModel.extractRegions(this.includedRegions);
        }

        if (rawIncludeRegions) {
            let included = false;
            if (mention.continentId && rawIncludeRegions.continents.includes(+mention.continentId)) {
                included = true;
            }
            if (mention.countryId && rawIncludeRegions.countries.includes(+mention.countryId)) {
                included = true;
            }
            if (mention.provinceId && rawIncludeRegions.provinces.includes(+mention.provinceId)) {
                included = true;
            }
            if (mention.countyId && rawIncludeRegions.counties.includes(+mention.countyId)) {
                included = true;
            }
            if (mention.cityId && rawIncludeRegions.cities.includes(+mention.cityId)) {
                included = true;
            }

            if (!included) {
                return false;
            }
        }

        let rawExcludeRegions: GeographyQueryModel = null;
        if (!MentionSearchQueryModel.isRawRegionEmpty(this.rawExcludedRegions)) {
            rawExcludeRegions = this.rawExcludedRegions;
        } else if (this.excludedRegions && this.excludedRegions.length > 0) {
            rawExcludeRegions = MentionSearchQueryModel.extractRegions(this.excludedRegions);
        }

        if (rawExcludeRegions) {
            if (mention.continentId && rawExcludeRegions.continents.includes(+mention.continentId)) {
                return false;
            }
            if (mention.countryId && rawExcludeRegions.countries.includes(+mention.countryId)) {
                return false;
            }
            if (mention.provinceId && rawExcludeRegions.provinces.includes(+mention.provinceId)) {
                return false;
            }
            if (mention.countyId && rawExcludeRegions.counties.includes(+mention.countyId)) {
                return false;
            }
            if (mention.cityId && rawExcludeRegions.cities.includes(+mention.cityId)) {
                return false;
            }
        }

        if (
            this.audienceReach &&
            this.rangeFilterNotEmpty(this.audienceReach) &&
            !this.matchRangeFilter(mention.audienceReach, this.audienceReach)
        ) {
            return false;
        }

        if (this.domainAuthority && !this.matchRangeFilter(mention.domainAuthority, this.domainAuthority)) {
            return false;
        }

        if (this.ave && !this.matchRangeFilter(aveUserCurrencyValue, this.ave)) {
            return false;
        }

        if (this.includedMediaTypes.length > 0) {
            const mediaType = mention.mediaType;
            const foundMediaType = this.includedMediaTypes.find((mt) => {
                return mt.id === mediaType;
            });
            if (!foundMediaType) {
                return false;
            }
        } else if (this.excludedMediaTypes.length) {
            const mediaType = mention.mediaType;
            const foundMediaType = this.excludedMediaTypes.find((mt) => {
                return mt.id === mediaType;
            });
            if (foundMediaType) {
                return false;
            }
        }

        if (this.sentiments.length > 0) {
            const sentiment = mention.sentiment;
            const sentimentQuery = this.mapSentimentToRanges(this.sentiments);
            let foundSentiment = false;

            if (sentiment === null || sentiment === undefined) {
                foundSentiment = sentimentQuery.includeUnset;
            } else {
                foundSentiment = !!sentimentQuery.ranges.find((range) => {
                    return range.min <= sentiment && range.max >= sentiment;
                });
            }
            if (!foundSentiment) {
                return false;
            }
        }

        if (this.folder.include.length > 0) {
            const folderIds = mention.folderIds;
            const queryFolderIds = this.folder.include;
            for (const queryfolderId of queryFolderIds) {
                if (folderIds.indexOf(queryfolderId) !== -1) {
                    return true;
                }
            }

            return false;
        }

        if (this.tag.include.length > 0) {
            const tagIds = mention.tagIds;
            const queryTagIds = this.tag.include;
            for (const queryTagId of queryTagIds) {
                if (tagIds.indexOf(queryTagId) !== -1) {
                    return true;
                }
            }

            return false;
        } else if(this.tag.exclude.length) {
            const tagIds = mention.tagIds;
            const queryTagIds = this.tag.exclude;
            for (const queryTagId of queryTagIds) {
                if (tagIds.indexOf(queryTagId) !== -1) {
                    return false;
                }
            }
        }

        // TODO : match prominence filter?

        // @TODO: matches tag groups?
        return true;
    }


    /**
     * returns a new MentionSearchQueryModel of the difference between 2 MentionSearchQueryModel
     */
    differenceInQueries(other: MentionSearchQueryModel): MentionSearchQueryModel {
        const query = new MentionSearchQueryModel();
        const clonedQuery = this.clone();
        if (!other) {
            return query;
        }

        const comparisonHelper = new ComparisonHelper();
        if (this.searchMode !== other.searchMode)  {
            query.searchMode = clonedQuery.searchMode;
        }
        if (!this.isExpertSearch()) {
            clonedQuery.keywordGroups.forEach(
                (keywordGroup: MonitoringKeywordGroupModel, index) => {
                    if (!keywordGroup.equals(other.keywordGroups[index])) {
                        query.keywordGroups.push(keywordGroup);
                    }
                }
            );
            other.keywordGroups.forEach(
                (keywordGroup: MonitoringKeywordGroupModel, index) => {
                    if (!keywordGroup.equals(clonedQuery.keywordGroups[index])) {
                        query.keywordGroups.push(keywordGroup);
                    }
                }
            );
        }
        if (this.isExpertSearch() && !this.expertBooleanQuery?.equals(other.expertBooleanQuery)) {
            query.expertBooleanQuery = clonedQuery.expertBooleanQuery;
        }
        if (!!this.caseSensitive !== !!other.caseSensitive) {
            query.caseSensitive = clonedQuery.caseSensitive;
        }
        ;
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedLanguages, other.includedLanguages, 'id')) {
            query.includedLanguages = xorBy(this.includedLanguages, other.includedLanguages, 'id');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedLanguages, other.excludedLanguages, 'id')) {
            query.excludedLanguages = xorBy(this.excludedLanguages, other.excludedLanguages, 'id');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedMediaTypes, other.includedMediaTypes, 'id')) {
            query.includedMediaTypes = xorBy(this.includedMediaTypes, other.includedMediaTypes, 'id');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedMediaTypes, other.excludedMediaTypes, 'id')) {
            query.excludedMediaTypes = xorBy(this.excludedMediaTypes, other.excludedMediaTypes, 'id');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedTweetTypes, other.includedTweetTypes, 'childId')) {
            query.includedTweetTypes = xorBy(this.includedTweetTypes, other.includedTweetTypes, 'childId');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedTweetTypes, other.excludedTweetTypes, 'childId')) {
            query.excludedTweetTypes = xorBy(this.excludedTweetTypes, other.excludedTweetTypes, 'childId');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedLinkedinTypes, other.includedLinkedinTypes, 'childId')) {
            query.includedLinkedinTypes = xorBy(this.includedLinkedinTypes, other.includedLinkedinTypes, 'childId');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedLinkedinTypes, other.excludedLinkedinTypes, 'childId')) {
            query.excludedLinkedinTypes = xorBy(this.excludedLinkedinTypes, other.excludedLinkedinTypes, 'childId');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedBlueskyTypes, other.includedBlueskyTypes, 'childId')) {
            query.includedBlueskyTypes = xorBy(this.includedBlueskyTypes, other.includedBlueskyTypes, 'childId');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedBlueskyTypes, other.excludedBlueskyTypes, 'childId')) {
            query.excludedBlueskyTypes = xorBy(this.excludedBlueskyTypes, other.excludedBlueskyTypes, 'childId');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedTvTypes, other.includedTvTypes, 'childId')) {
            query.includedTvTypes = xorBy(this.includedTvTypes, other.includedTvTypes, 'childId');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedTvTypes, other.excludedTvTypes, 'childId')) {
            query.excludedTvTypes = xorBy(this.excludedTvTypes, other.excludedTvTypes, 'childId');
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.sentiments, other.sentiments, 'id')) {
            query.sentiments = xorBy(this.sentiments, other.sentiments, 'id');
        }

        // Need to be able to compare raw to non-raw regions, so convert all to raw for comparison
        const selfRegionsAreStillRaw = (
            (this.includedRegions.length === 0 && !MentionSearchQueryModel.isRawRegionEmpty(this.rawIncludedRegions))
            || (this.excludedRegions.length === 0 && !MentionSearchQueryModel.isRawRegionEmpty(this.rawExcludedRegions))
        );
        const otherRegionsAreStillRaw = (
            (other.includedRegions.length === 0 && !MentionSearchQueryModel.isRawRegionEmpty(other.rawIncludedRegions))
            || (other.excludedRegions.length === 0 && !MentionSearchQueryModel.isRawRegionEmpty(other.rawExcludedRegions))
        );
        const selfRawIncludes = selfRegionsAreStillRaw && this.rawIncludedRegions ?
            this.rawIncludedRegions : MentionSearchQueryModel.extractRegions(this.includedRegions);
        const selfRawExcludes = selfRegionsAreStillRaw && this.rawExcludedRegions ?
            this.rawExcludedRegions : MentionSearchQueryModel.extractRegions(this.excludedRegions);
        const otherRawIncludes = otherRegionsAreStillRaw && other.rawIncludedRegions ?
            other.rawIncludedRegions : MentionSearchQueryModel.extractRegions(other.includedRegions);
        const otherRawExcludes = otherRegionsAreStillRaw && other.rawExcludedRegions ?
            other.rawExcludedRegions : MentionSearchQueryModel.extractRegions(other.excludedRegions);

        if (!isEqual(selfRawIncludes, otherRawIncludes)) {
            query.rawIncludedRegions = clonedQuery.rawIncludedRegions;
        }
        if (!isEqual(selfRawExcludes, otherRawExcludes)) {
            query.rawExcludedRegions = clonedQuery.rawExcludedRegions;
        }

        let thisSimpleArray = this.includedSources.map( source => {
            if (source.entityType !== SearchCriteriaSourcesEnum.sourceGroups) {
                return JSON.stringify(source);
            } else {
                return source.entityName;
            }
        });
        let otherSimpleArray = other.includedSources.map( source => {
            if (source.entityType !== SearchCriteriaSourcesEnum.sourceGroups) {
                return JSON.stringify(source);
            } else {
                return source.entityName;
            }
        });
        if (!comparisonHelper.compareObjectsArraysForEquality(thisSimpleArray, otherSimpleArray)) {
            query.includedSources = clonedQuery.includedSources;
        }

        thisSimpleArray = this.excludedSources.map( source => {
            if (source.entityType !== SearchCriteriaSourcesEnum.sourceGroups) {
                return JSON.stringify(source);
            } else {
                return source.entityName;
            }
        });
        otherSimpleArray = other.excludedSources.map( source => {
            if (source.entityType !== SearchCriteriaSourcesEnum.sourceGroups) {
                return JSON.stringify(source);
            } else {
                return source.entityName;
            }
        });

        if (!comparisonHelper.compareObjectsArraysForEquality(thisSimpleArray, otherSimpleArray)) {
            query.excludedSources = clonedQuery.excludedSources;
        }

        const thisFilterSourceArray = this.filterSources ? this.filterSources.filter( source => {
            if (source.type !== SourceTypeEnum.sourceGroup) {
                return JSON.stringify(source);
            }
        }) : [];

        const otherFilterSourceArray = other.filterSources ? other.filterSources.map( source => {
            if (source.type !== SourceTypeEnum.sourceGroup) {
                return JSON.stringify(source);
            }
        }) : [];

        if (!comparisonHelper.compareObjectsArraysForEquality(thisFilterSourceArray, otherFilterSourceArray)) {
            query.filterSources = clonedQuery.filterSources;
        }

        if (!MentionSearchQueryModel.rangeModelEquals(this.audienceReach, other.audienceReach)) {
            query.audienceReach = clonedQuery.audienceReach;
        }

        engagementFilterApiKeys.forEach(key => {
            if (!MentionSearchQueryModel.rangeModelEquals(this.engagementFilter[key], other.engagementFilter[key])) {
                query.engagementFilter[key] = clonedQuery.engagementFilter[key];
            }
        });

        if (!MentionSearchQueryModel.rangeModelEquals(this.ave, other.ave)) {
            query.ave = clonedQuery.ave;
        }
        if (!isEqual(this.folder, other.folder)) {
            query.folder = clonedQuery.folder;
        }
        if (
            !!this.domainAuthority !== !!other.domainAuthority ||
            this.domainAuthority?.min !== other.domainAuthority?.min ||
            this.domainAuthority?.max !== other.domainAuthority?.max
        ) {
            query.domainAuthority = clonedQuery.domainAuthority;
        }
        if (!isEqual(this.tag, other.tag)) {
            query.tag = clonedQuery.tag;
        }
        if (!isEqual(this.agent, other.agent)) {
            query.agent = clonedQuery.agent;
        }
        if (!isEqual(this.subscription, other.subscription)) {
            query.subscription = clonedQuery.subscription;
        }
        if (!isEqual(this.theme, other.theme)) {
            query.theme = clonedQuery.theme;
        }
        if (!isEqual(this.aiAgent, other.aiAgent)) {
            query.aiAgent = clonedQuery.aiAgent;
        }
        if (!isEqual(this.aiAgentSource, other.aiAgentSource)) {
            query.aiAgentSource = clonedQuery.aiAgentSource;
        }
        if (!isEqual(this.aiAgentAdhoc, other.aiAgentAdhoc)) {
            query.aiAgentAdhoc = clonedQuery.aiAgentAdhoc;
        }
        if (!isEqual(this.tagGroup, other.tagGroup)) {
            query.tagGroup = clonedQuery.tagGroup;
        }
        if (!isEqual(this.sourceGroup, other.sourceGroup)) {
            query.sourceGroup = clonedQuery.sourceGroup;
        }

        if (!isEqual(this.hashtags, other.hashtags)) {
            query.hashtags = clonedQuery.hashtags;
        }

        if (this.includedProminenceFilter?.length || other.includedProminenceFilter?.length) {
            if (!isEqual(this.includedProminenceFilter, other.includedProminenceFilter)) {
                query.includedProminenceFilter = clonedQuery.includedProminenceFilter;
            }
        }

        if (this.excludedProminenceFilter?.length || other.excludedProminenceFilter?.length) {
            if (!isEqual(this.excludedProminenceFilter, other.excludedProminenceFilter)) {
                query.excludedProminenceFilter = clonedQuery.excludedProminenceFilter;
            }
        }

        if (this.discoverListFilter) {
            if (!this.discoverListFilter.equals(other.discoverListFilter)) {
                query.discoverListFilter = clonedQuery.discoverListFilter;
            }
        }

        if (!isEqual(this.sampleFilter, other.sampleFilter)) {
            query.sampleFilter = clonedQuery.sampleFilter;
        }
        return query;
    }

    /**
     * Compares properties on this object to another searchQueryModel
     *
     * @param other Object to be compared to
     */
    equals(other: MentionSearchQueryModel, ignoreDate: boolean = false): boolean {
        if (!other) {
            return false;
        }

        const comparisonHelper = new ComparisonHelper();
        if (this.searchMode !== other.searchMode)  {
            return false;
        }
        if (!this.isExpertSearch()) {
            if (
                this.keywordGroups
                && other.keywordGroups
                && this.keywordGroups.length !== other.keywordGroups.length
            ) {
                return false;
            }

            let changesFound = false;
            this.keywordGroups.forEach(
                (keywordGroup: MonitoringKeywordGroupModel, index) => {
                    if (!keywordGroup.equals(other.keywordGroups[index])) {
                        changesFound = true;
                        return;
                    }
                }
            );
            if (changesFound) {
                return false;
            }
        }
        if (this.isExpertSearch() && !this.expertBooleanQuery?.equals(other.expertBooleanQuery)) {
            return false;
        }
        if (!!this.caseSensitive !== !!other.caseSensitive) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedLanguages, other.includedLanguages, 'id')) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedLanguages, other.excludedLanguages, 'id')) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedMediaTypes, other.includedMediaTypes, 'id')) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedMediaTypes, other.excludedMediaTypes, 'id')) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedTweetTypes, other.includedTweetTypes, 'childId')) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedTweetTypes, other.excludedTweetTypes, 'childId')) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedLinkedinTypes, other.includedLinkedinTypes, 'childId')) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedLinkedinTypes, other.excludedLinkedinTypes, 'childId')) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.includedBlueskyTypes, other.includedBlueskyTypes, 'childId')) {
            return false;
        }
        if (!comparisonHelper.compareObjectsArraysForEquality(this.excludedBlueskyTypes, other.excludedBlueskyTypes, 'childId')) {
            return false;
        }

        // For existing topics, if TV type is included, no broadcast subtypes included is the same as all broadcast subtypes included.
        const tvTypeIncluded = this.includedMediaTypes?.find(mediaType => mediaType.id === MediaTypeEnum.TV)
            && other.includedMediaTypes?.find(mediaType => mediaType.id === MediaTypeEnum.TV);
        const thisIncludedTvTypes = tvTypeIncluded && !this.includedTvTypes?.length
            ? MediaTypeModel.getTvSubTypes()
            : this.includedTvTypes;
        const otherIncludedTvTypes = tvTypeIncluded && !other.includedTvTypes?.length
            ? MediaTypeModel.getTvSubTypes()
            : other.includedTvTypes;
        if (!comparisonHelper.compareObjectsArraysForEquality(thisIncludedTvTypes, otherIncludedTvTypes, 'childId')) {
            return false;
        }

        // Same for excluding TV type
        const tvTypeExcluded = this.excludedMediaTypes?.find(mediaType => mediaType.id === MediaTypeEnum.TV)
            && other.excludedMediaTypes?.find(mediaType => mediaType.id === MediaTypeEnum.TV);
        const thisExcludedTvTypes = tvTypeExcluded && !this.excludedTvTypes?.length
            ? MediaTypeModel.getTvSubTypes()
            : this.excludedTvTypes;
        const otherExcludedTvTypes = tvTypeExcluded && !other.excludedTvTypes?.length
            ? MediaTypeModel.getTvSubTypes()
            : other.excludedTvTypes;
        if (!comparisonHelper.compareObjectsArraysForEquality(thisExcludedTvTypes, otherExcludedTvTypes, 'childId')) {
            return false;
        }

        if (!comparisonHelper.compareObjectsArraysForEquality(this.sentiments, other.sentiments, 'id')) {
            return false;
        }
        // Need to be able to compare raw to non-raw regions, so convert all to raw for comparison
        const selfRegionsAreStillRaw = (
            (this.includedRegions.length === 0 && !MentionSearchQueryModel.isRawRegionEmpty(this.rawIncludedRegions))
            || (this.excludedRegions.length === 0 && !MentionSearchQueryModel.isRawRegionEmpty(this.rawExcludedRegions))
        );
        const otherRegionsAreStillRaw = (
            (other.includedRegions.length === 0 && !MentionSearchQueryModel.isRawRegionEmpty(other.rawIncludedRegions))
            || (other.excludedRegions.length === 0 && !MentionSearchQueryModel.isRawRegionEmpty(other.rawExcludedRegions))
        );
        const selfRawIncludes = selfRegionsAreStillRaw && this.rawIncludedRegions ?
            this.rawIncludedRegions : MentionSearchQueryModel.extractRegions(this.includedRegions);
        const selfRawExcludes = selfRegionsAreStillRaw && this.rawExcludedRegions ?
            this.rawExcludedRegions : MentionSearchQueryModel.extractRegions(this.excludedRegions);
        const otherRawIncludes = otherRegionsAreStillRaw && other.rawIncludedRegions ?
            other.rawIncludedRegions : MentionSearchQueryModel.extractRegions(other.includedRegions);
        const otherRawExcludes = otherRegionsAreStillRaw && other.rawExcludedRegions ?
            other.rawExcludedRegions : MentionSearchQueryModel.extractRegions(other.excludedRegions);

        if (!isEqual(selfRawIncludes, otherRawIncludes)) {
            return false;
        }
        if (!isEqual(selfRawExcludes, otherRawExcludes)) {
            return false;
        }


        let thisSimpleArray = this.includedSources.map( source => {
            if (source.entityType !== SearchCriteriaSourcesEnum.sourceGroups) {
                return JSON.stringify(source);
            } else {
                return source.entityName;
            }
        });
        let otherSimpleArray = other.includedSources.map( source => {
            if (source.entityType !== SearchCriteriaSourcesEnum.sourceGroups) {
                return JSON.stringify(source);
            } else {
                return source.entityName;
            }
        });
        if (!comparisonHelper.compareObjectsArraysForEquality(thisSimpleArray, otherSimpleArray)) {
            return false;
        }

        thisSimpleArray = this.excludedSources.map( source => {
            if (source.entityType !== SearchCriteriaSourcesEnum.sourceGroups) {
                return JSON.stringify(source);
            } else {
                return source.entityName;
            }
        });
        otherSimpleArray = other.excludedSources.map( source => {
            if (source.entityType !== SearchCriteriaSourcesEnum.sourceGroups) {
                return JSON.stringify(source);
            } else {
                return source.entityName;
            }
        });

        if (!comparisonHelper.compareObjectsArraysForEquality(thisSimpleArray, otherSimpleArray)) {
            return false;
        }

        const thisFilterSourceArray = this.filterSources ? this.filterSources.filter( source => {
            if (source.type !== SourceTypeEnum.sourceGroup) {
                return JSON.stringify(source);
            }
        }) : [];

        const otherFilterSourceArray = other.filterSources ? other.filterSources.map( source => {
            if (source.type !== SourceTypeEnum.sourceGroup) {
                return JSON.stringify(source);
            }
        }) : [];

        if (!comparisonHelper.compareObjectsArraysForEquality(thisFilterSourceArray, otherFilterSourceArray)) {
            return false;
        }

        if (!MentionSearchQueryModel.rangeModelEquals(this.audienceReach, other.audienceReach)) {
            return false;
        }

        for (const key of engagementFilterApiKeys) {
            if (!MentionSearchQueryModel.rangeModelEquals(this.engagementFilter[key], other.engagementFilter[key])) {
                return false;
            }
        }

        if (!MentionSearchQueryModel.rangeModelEquals(this.ave, other.ave)) {
            return false;
        }
        if (!isEqual(this.folder, other.folder)) {
            return false;
        }
        if (
            !!this.domainAuthority !== !!other.domainAuthority ||
            this.domainAuthority?.min !== other.domainAuthority?.min ||
            this.domainAuthority?.max !== other.domainAuthority?.max
        ) {
            return false;
        }
        if (!isEqual(this.tag, other.tag)) {
            return false;
        }
        if (!isEqual(this.agent, other.agent)) {
            return false;
        }
        if (!isEqual(this.subscription, other.subscription)) {
            return false;
        }
        if (!isEqual(this.theme, other.theme)) {
            return false;
        }
        if (!isEqual(this.aiAgent, other.aiAgent)) {
            return false;
        }
        if (!isEqual(this.aiAgentSource, other.aiAgentSource)) {
            return false;
        }
        if (!isEqual(this.aiAgentAdhoc, other.aiAgentAdhoc)) {
            return false;
        }
        if (!isEqual(this.tagGroup, other.tagGroup)) {
            return false;
        }
        if (!isEqual(this.sourceGroup, other.sourceGroup)) {
            return false;
        }

        if (!isEqual(this.hashtags, other.hashtags)) {
            return false;
        }

        if (this.includedProminenceFilter?.length || other.includedProminenceFilter?.length) {
            if (!isEqual(this.includedProminenceFilter, other.includedProminenceFilter)) {
                return false;
            }
        }

        if (this.excludedProminenceFilter?.length || other.excludedProminenceFilter?.length) {
            if (!isEqual(this.excludedProminenceFilter, other.excludedProminenceFilter)) {
                return false;
            }
        }

        if (this.discoverListFilter ) {
            if (!this.discoverListFilter.equals(other.discoverListFilter)) {
                return false;
            }
        }

        if (!ignoreDate && !DateQueryModel.equals(this.dateQuery, other.dateQuery)) {
            return false;
        }

        if (!isEqual(this.sampleFilter, other.sampleFilter)) {
            return false;
        }
        return true;
    }

    isEmpty(): boolean {
        if (this.isExpertSearch()) {
            if (this.expertBooleanQuery.filter) {
                return false;
            }
        } else {
            const notEmptyKeywordGroup = this.keywordGroups.find(group => group.isAnyKeywordSet());
            if (notEmptyKeywordGroup) {
                return false;
            }
        }

        if (this.includedLanguages && this.includedLanguages.length > 0) {
            return false;
        }
        if (this.excludedLanguages && this.excludedLanguages.length > 0) {
            return false;
        }

        if (this.includedMediaTypes && this.includedMediaTypes.length > 0) {
            return false;
        }
        if (this.excludedMediaTypes && this.excludedMediaTypes.length > 0) {
            return false;
        }
        if (this.includedTweetTypes && this.includedTweetTypes.length > 0) {
            return false;
        }
        if (this.excludedTweetTypes && this.excludedTweetTypes.length > 0) {
            return false;
        }
        if (this.includedLinkedinTypes && this.includedLinkedinTypes.length > 0) {
            return false;
        }
        if (this.excludedLinkedinTypes && this.excludedLinkedinTypes.length > 0) {
            return false;
        }
        if (this.includedBlueskyTypes && this.includedBlueskyTypes.length > 0) {
            return false;
        }
        if (this.excludedBlueskyTypes && this.excludedBlueskyTypes.length > 0) {
            return false;
        }
        if (this.includedTvTypes && this.includedTvTypes.length > 0) {
            return false;
        }
        if (this.excludedTvTypes && this.excludedTvTypes.length > 0) {
            return false;
        }
        if (this.sentiments && this.sentiments.length > 0) {
            return false;
        }
        if (
            this.audienceReach
            && (
                (this.audienceReach.min !== null && this.audienceReach.min !== undefined)
                || (this.audienceReach.max !== null && this.audienceReach.max !== undefined)
            )
        ) {
            return false;
        }
        if (
            this.ave
            && (
                (this.ave.min !== null && this.ave.min !== undefined)
                || (this.ave.max !== null && this.ave.max !== undefined)
            )
        ) {
            return false;
        }

        if (
            this.domainAuthority
            && (
                (this.domainAuthority.min !== null && this.domainAuthority.min !== undefined)
                || (this.domainAuthority.max !== null && this.domainAuthority.max !== undefined)
            )
        ) {
            return false;
        }

        // Need to be able to compare raw to non-raw regions, so convert all to raw for comparison
        if (
            (this.includedRegions.length > 0 || !MentionSearchQueryModel.isRawRegionEmpty(this.rawIncludedRegions))
            || (this.excludedRegions.length > 0 || !MentionSearchQueryModel.isRawRegionEmpty(this.rawExcludedRegions))
        ) {
            return false;
        }

        if (this.includedSources && this.includedSources.length > 0) {
            return false;
        }
        if (this.excludedSources && this.excludedSources.length > 0) {
            return false;
        }

        if (this.tag && (this.tag.include.length > 0 || this.tag.exclude.length > 0)) {
            return false;
        }
        if (this.agent && (this.agent.include.length > 0 || this.agent.exclude.length > 0)) {
            return false;
        }
        if (this.subscription && (this.subscription.include.length > 0 || this.subscription.exclude.length > 0)) {
            return false;
        }
        if (this.theme && (this.theme.include.length > 0 || this.theme.exclude.length > 0)) {
            return false;
        }
        if (this.aiAgent && (this.aiAgent.include.length > 0 || this.aiAgent.exclude.length > 0)) {
            return false;
        }
        if (this.aiAgentSource && (this.aiAgentSource.include.length > 0 || this.aiAgentSource.exclude.length > 0)) {
            return false;
        }
        if (this.aiAgentAdhoc && (this.aiAgentAdhoc.include.length > 0 || this.aiAgentAdhoc.exclude.length > 0)) {
            return false;
        }
        if (this.tagGroup && (this.tagGroup.include.length > 0 || this.tagGroup.exclude.length > 0)) {
            return false;
        }
        if (this.sourceGroup && (this.sourceGroup.include.length > 0 || this.sourceGroup.exclude.length > 0)) {
            return false;
        }
        if (this.folder && (this.folder.include.length > 0 || this.folder.exclude.length > 0)) {
            return false;
        }
        if (this.hashtags && (this.hashtags.include.length > 0 || this.hashtags.exclude.length > 0)) {
            return false;
        }
        if (this.includedProminenceFilter?.length > 0 || this.excludedProminenceFilter?.length > 0) {
            return false;
        }

        for (const key of engagementFilterApiKeys) {
            // when you call applySearchQueryToFilters in MentionsNoFacetFilterService,
            // it will set the engagementRate to {max: undefined, min: undefined}, even though it passes {}
            // so it doesn't think it is empty anymore
            if (key === 'engagementRate') {
                if (this.engagementFilter?.[key] &&
                    (this.engagementFilter?.[key]?.min !== null && this.engagementFilter?.[key]?.min !== undefined)
                    || (this.engagementFilter?.[key]?.max !== null && this.engagementFilter?.[key]?.max !== undefined)
                ) {
                    return false;
                }
            } else if (this.engagementFilter?.[key] && !isEmpty(this.engagementFilter?.[key])) {
                return false;
            }
        }

        if (this.discoverListFilter) {
            return false;
        }

        return true;
    }

    clone(keepHydratedRegions: boolean = false): MentionSearchQueryModel {
        const clone = new MentionSearchQueryModel(this.getApiEntity());

        // getApiEntity() dehydrates regions to raw ids; preserve the hydrated objects when requested.
        if (keepHydratedRegions && (this.includedRegions.length || this.excludedRegions.length)) {
            clone.includedRegions = this.includedRegions.map(region => region.clone());
            clone.excludedRegions = this.excludedRegions.map(region => region.clone());
            clone.rawIncludedRegions = this.rawIncludedRegions;
            clone.rawExcludedRegions = this.rawExcludedRegions;
        }

        return clone;
    }

    public isQueryTooLong(): boolean {
        let query: MonitoringAdvancedBooleanModel;
        if (this.isExpertSearch()) {
            query = this.expertBooleanQuery;
        } else {
            query = new MonitoringAdvancedBooleanModel();
            query.mapKeywordGroupQueryToExpertBooleanQuery(
                this.keywordGroups,
                true
            );
        }

        return query.isQueryTooLong();
    }
    public isQueryTooShort(): boolean {
        let query: MonitoringAdvancedBooleanModel;
        if (this.isExpertSearch()) {
            query = this.expertBooleanQuery;
        } else {
            query = new MonitoringAdvancedBooleanModel();
            query.mapKeywordGroupQueryToExpertBooleanQuery(
                this.keywordGroups,
                true
            );
        }
        return query.isQueryTooShort();
    }

    private rangeFilterNotEmpty(filter: RangeModel): boolean {
        if (!filter) {
            return false;
        }

        // We started storing a max of 999,999,999 for impression when it was undefined,
        // so here we have to check Mention.maxAudienceReach to treat it as undefined
        return (!!filter.min) || (!!filter.max && filter.max !== Mention.maxAudienceReach);
    }

    private matchRangeFilter(value: number, filter: RangeModel): boolean {
        // Filter isn't actually set
        if (filter.min === undefined && filter.max === undefined) {
            return true;
        }

        // if the value is not a valid number like null, and we already checked Filter is set, then return false
        // Scenario: a mention with null as ave, it should not be treated as a fit for filter ave < 100
        if (value === null || value === undefined) {
            if (isNaN(filter.min) && isNaN(filter.max)) {
                return true;
            }
            return false;
        }

        if (!isNaN(filter.min)) {
            if (value < filter.min) {
                return false;
            }
        }

        if (!isNaN(filter.max)) {
            if (value > filter.max) {
                return false;
            }
        }

        return true;
    }

    isUnsavedSearchValid(): boolean {
        const includeSources =
            !!this.sourceGroup?.include?.length
            || !!this.includedSources?.length
            || !!this.discoverListFilter?.discoverList?.include?.length;

        const hasKeyword = this.hasPositiveKeywordGroups();
        const hasValidExpertKeyword = !!this.expertBooleanQuery.filter?.trim().length;

        return includeSources || hasKeyword || hasValidExpertKeyword;
    }
}
